import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft, Upload, Download, Trash2, FileText, Mail, Send,
  Activity, Calendar, DollarSign, Users, MapPin, Phone,
  Clock, CheckCircle, PauseCircle, XCircle,
  File, Image, FileSpreadsheet, Plus, ShoppingCart, Receipt,
  Eye, ChevronDown, AlertTriangle, Ban, Sparkles, Wand2, Loader2, Brain, UserCheck, Edit, GripVertical, Copy, Link, Pencil, FileInput, ExternalLink, FilePlus2
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { jobsAPI, documentsAPI, quotesAPI, contractorsAPI, invoicesAPI, tradeAPI, purchaseOrdersAPI, aiAPI, pricingAPI, quoteTemplatesAPI, emailAPI, brandingAPI } from '../services/api';
import { DatePicker } from '../components/DatePicker';
import { PoBudgetBurndown } from '../components/PoBudgetBurndown';
import { InvoicesTab } from '../components/InvoicesTab';
import { GeneratePoFromJobDialog } from '../components/GeneratePoFromJobDialog';

// Sortable wrapper for quote sections: exposes drag handle props via a
// render-prop so the AccordionTrigger can capture pointer events on the
// GripVertical without hijacking accordion clicks.
const SortableSectionRow = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
};

// Job document categories
const jobDocumentTabs = [
  { id: 'general', label: 'General' },
  { id: 'photos', label: 'Photos' },
  { id: 'client_po', label: 'Client POs' },
  { id: 'quotes_variations', label: 'Quotes/Variations' },
  { id: 'reports', label: 'Reports' },
  { id: 'contractor_quotes', label: 'Contractor Quotes' },
  { id: 'purchase_orders', label: 'POs' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'certification_forms', label: 'Certification/Forms' },
  { id: 'emails', label: 'Emails' },
  { id: 'whs', label: 'WH&S' },
];

// Quote section labels
const sectionLabels = {
  preliminaries: 'Preliminaries',
  internal: 'Internal',
  external: 'External',
  kitchen: 'Kitchen',
  bathroom: 'Bathroom',
  bedroom: 'Bedroom',
  living: 'Living Room',
  laundry: 'Laundry',
  garage: 'Garage',
  outdoor: 'Outdoor',
  custom: 'Custom'
};

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400', icon: Activity },
  completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  on_hold: { label: 'On Hold', color: 'bg-orange-500/20 text-orange-400', icon: PauseCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  invoiced: { label: 'Invoiced', color: 'bg-purple-500/20 text-purple-400', icon: Receipt },
  draft: { label: 'Draft', color: 'bg-gray-500/20 text-gray-400', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-500/20 text-blue-400', icon: Send },
  accepted: { label: 'Accepted', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  paid: { label: 'Paid', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  acknowledged: { label: 'Acknowledged', color: 'bg-blue-500/20 text-blue-400', icon: CheckCircle },
};

// Sortable Item Component for drag and drop
const SortableQuoteItem = ({ id, item, itemIdx, sectionIdx, updateSectionItem, removeItemFromSection, canDelete, tradeTypes, unitTypes, onAddUnit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [showAddUnit, setShowAddUnit] = useState(false);
  const [newUnit, setNewUnit] = useState('');

  const handleAddUnit = () => {
    if (newUnit.trim()) {
      onAddUnit(newUnit.trim());
      updateSectionItem(sectionIdx, itemIdx, 'unit', newUnit.trim());
      setNewUnit('');
      setShowAddUnit(false);
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="grid grid-cols-12 gap-2 items-center bg-card">
      <div className="col-span-1 flex items-center justify-center cursor-grab" {...attributes} {...listeners}>
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="col-span-3">
        <Input value={item.description} onChange={(e) => updateSectionItem(sectionIdx, itemIdx, 'description', e.target.value)} placeholder="Item description" className="bg-background text-sm" />
      </div>
      <div className="col-span-1">
        <Select value={item.trade_type} onValueChange={(v) => updateSectionItem(sectionIdx, itemIdx, 'trade_type', v)}>
          <SelectTrigger className="bg-background text-sm h-9 px-2"><SelectValue placeholder="Trade" /></SelectTrigger>
          <SelectContent>{tradeTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="col-span-1">
        <Input type="number" value={item.quantity} onChange={(e) => updateSectionItem(sectionIdx, itemIdx, 'quantity', e.target.value)} placeholder="Qty" className="bg-background text-sm" />
      </div>
      <div className="col-span-1">
        {showAddUnit ? (
          <div className="flex gap-1">
            <Input 
              value={newUnit} 
              onChange={(e) => setNewUnit(e.target.value)} 
              placeholder="Unit" 
              className="bg-background text-sm h-9 w-16"
              onKeyDown={(e) => e.key === 'Enter' && handleAddUnit()}
              autoFocus
              data-testid={`unit-input-${sectionIdx}-${itemIdx}`}
            />
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={handleAddUnit}><Plus className="h-3 w-3" /></Button>
          </div>
        ) : (
          <Select value={item.unit || 'Item'} onValueChange={(v) => v === '__add__' ? setShowAddUnit(true) : updateSectionItem(sectionIdx, itemIdx, 'unit', v)}>
            <SelectTrigger className="bg-background text-sm h-9 px-2" data-testid={`unit-select-${sectionIdx}-${itemIdx}`}><SelectValue placeholder="Unit" /></SelectTrigger>
            <SelectContent>
              {unitTypes.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              <SelectItem value="__add__" className="text-primary"><Plus className="h-3 w-3 inline mr-1" />Add new</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="col-span-2">
        <Input type="number" value={item.unit_price} onChange={(e) => updateSectionItem(sectionIdx, itemIdx, 'unit_price', e.target.value)} placeholder="Price" className="bg-background text-sm" />
      </div>
      <div className="col-span-2 text-right font-mono text-sm">${(parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0)).toFixed(2)}</div>
      <div className="col-span-1">
        <Button variant="ghost" size="icon" onClick={() => removeItemFromSection(sectionIdx, itemIdx)} disabled={!canDelete}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

const getFileIcon = (mimeType, filename) => {
  if (mimeType?.startsWith('image/')) return <Image className="h-4 w-4 text-blue-400" />;
  if (mimeType === 'application/pdf') return <FileText className="h-4 w-4 text-red-400" />;
  if (mimeType?.includes('spreadsheet') || filename?.endsWith('.csv')) return <FileSpreadsheet className="h-4 w-4 text-green-400" />;
  return <File className="h-4 w-4 text-gray-400" />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobData, setJobData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeDocTab, setActiveDocTab] = useState('general');
  
  // DnD sensors - must be at top level of component
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  const [documents, setDocuments] = useState([]);
  const [clientPoDoc, setClientPoDoc] = useState(null);
  const [margin, setMargin] = useState(null);
  const [activity, setActivity] = useState([]);
  const [emails, setEmails] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [tradeTypes, setTradeTypes] = useState([]);
  const [unitTypes, setUnitTypes] = useState(['Item', 'm2', 'lm', 'pc', 'ps', 'hour', 'day', 'sqm', 'm', 'kg', 'L', 'set', 'lot', 'allow']);
  const [quoteSections, setQuoteSections] = useState([]);
  const [commonPOItems, setCommonPOItems] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState({});
  
  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');

  // Email dialog
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailData, setEmailData] = useState({ template: '', recipient: '', subject: '', body: '', customMessage: '' });
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [emailFileUploads, setEmailFileUploads] = useState([]);
  const [availableAttachments, setAvailableAttachments] = useState([]);
  const [attachmentsDialogOpen, setAttachmentsDialogOpen] = useState(false);

  // Quote dialog with sections
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [quoteSectionsData, setQuoteSectionsData] = useState([
    { section_type: 'preliminaries', section_name: '', items: [{ description: '', quantity: 1, unit: 'Item', unit_price: 0, trade_type: 'General' }] }
  ]);
  const [pricingItems, setPricingItems] = useState([]);
  const [selectedPricingCategory, setSelectedPricingCategory] = useState('all');
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [quotePaymentTerms, setQuotePaymentTerms] = useState('');
  const [quotePaymentDetails, setQuotePaymentDetails] = useState('');
  const [quoteChargeGst, setQuoteChargeGst] = useState(true);
  const [companyChargesGst, setCompanyChargesGst] = useState(true);
  // "Import from another quote" picker (inside the Create/Edit Quote dialog).
  const [importFromQuoteOpen, setImportFromQuoteOpen] = useState(false);
  const [allTenantQuotes, setAllTenantQuotes] = useState([]);
  const [importQuoteSearch, setImportQuoteSearch] = useState('');

  // Quote view dialog
  const [viewQuoteOpen, setViewQuoteOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  // Invoice view is now owned by InvoicesTab component

  // PO from Quote dialog
  const [poOpen, setPoOpen] = useState(false);
  const [poQuote, setPoQuote] = useState(null);
  const [poContractor, setPoContractor] = useState('');
  const [poTradeFilter, setPoTradeFilter] = useState('all');
  const [additionalItems, setAdditionalItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({}); // {itemIndex: {selected: true, quantity: x, split: [{contractor_id, quantity, amount}]}}
  const [itemAllocations, setItemAllocations] = useState([]); // [{itemIndex, contractor_id, quantity, unit_price, description}]

  // PO Cancel dialog
  const [cancelPoOpen, setCancelPoOpen] = useState(false);
  const [cancellingPo, setCancellingPo] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [sendCancelEmail, setSendCancelEmail] = useState(true);

  // PO Edit dialog
  const [editPoOpen, setEditPoOpen] = useState(false);
  const [editingPo, setEditingPo] = useState(null);
  const [editPoItems, setEditPoItems] = useState([]);

  // PO Send dialog
  const [sendPoOpen, setSendPoOpen] = useState(false);
  const [sendingPo, setSendingPo] = useState(null);
  const [poEmailMessage, setPoEmailMessage] = useState('');
  const [poSending, setPoSending] = useState(false);

  // Import PDF dialog
  const [importPdfOpen, setImportPdfOpen] = useState(false);
  const [importPdfFile, setImportPdfFile] = useState(null);
  const [importPdfLoading, setImportPdfLoading] = useState(false);

  // Generate PO from Job dialog (POs tab)
  const [genPoFromJobOpen, setGenPoFromJobOpen] = useState(false);

  // Edit Job dialog
  const [editJobOpen, setEditJobOpen] = useState(false);
  const [editJobData, setEditJobData] = useState({
    title: '',
    description: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    address: '',
    start_date: '',
    end_date: '',
    budget: '',
    is_roof: false,
    client_prefix: ''
  });
  const [editJobLoading, setEditJobLoading] = useState(false);

  // Send Client Portal Link dialog
  const [sendLinkOpen, setSendLinkOpen] = useState(false);
  const [sendLinkLoading, setSendLinkLoading] = useState(false);

  // AI Features state
  const [aiSuggestOpen, setAiSuggestOpen] = useState(false);
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  const [aiTradeSuggestions, setAiTradeSuggestions] = useState(null);
  const [aiCostOpen, setAiCostOpen] = useState(false);
  const [aiCostLoading, setAiCostLoading] = useState(false);
  const [aiCostEstimate, setAiCostEstimate] = useState(null);
  const [aiEmailOpen, setAiEmailOpen] = useState(false);
  const [aiEmailLoading, setAiEmailLoading] = useState(false);
  const [aiEmailDraft, setAiEmailDraft] = useState(null);
  const [aiEmailType, setAiEmailType] = useState('quote_followup');
  const [aiEmailRecipient, setAiEmailRecipient] = useState('');

  const fetchJobData = useCallback(async () => {
    try {
      setLoading(true);
      const [jobRes, contractorsRes, tradesRes, sectionsRes, poItemsRes, templatesRes, pricingRes, quoteTemplatesRes, unitTypesRes] = await Promise.all([
        jobsAPI.getFull(jobId),
        contractorsAPI.getAll(),
        tradeAPI.getTradeTypes(),
        tradeAPI.getQuoteSections(),
        tradeAPI.getCommonPOItems(),
        tradeAPI.getEmailTemplates(),
        pricingAPI.getAll(),
        quoteTemplatesAPI.getAll(),
        tradeAPI.getUnitTypes().catch(() => ({ data: ['Item', 'm2', 'lm', 'pc', 'ps', 'hour', 'day'] }))
      ]);
      setJobData(jobRes.data);
      setContractors(contractorsRes.data);
      setTradeTypes(tradesRes.data);
      setQuoteSections(sectionsRes.data);
      setCommonPOItems(poItemsRes.data);
      setEmailTemplates(templatesRes.data);
      setPricingItems(pricingRes.data || []);
      setQuoteTemplates(quoteTemplatesRes.data || []);
      setUnitTypes(unitTypesRes.data || ['Item', 'm2', 'lm', 'pc', 'ps', 'hour', 'day']);
    } catch (error) {
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const fetchDocuments = useCallback(async (docType = null) => {
    try {
      const params = docType ? { document_type: docType } : {};
      const response = await jobsAPI.getDocuments(jobId, params);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  }, [jobId]);

  const fetchActivity = useCallback(async () => {
    try {
      const response = await jobsAPI.getActivity(jobId);
      setActivity(response.data);
    } catch (error) {
      console.error('Failed to load activity:', error);
    }
  }, [jobId]);

  const fetchEmails = useCallback(async () => {
    try {
      const response = await jobsAPI.getEmails(jobId);
      setEmails(response.data);
    } catch (error) {
      console.error('Failed to load emails:', error);
    }
  }, [jobId]);

  const fetchClientPoDoc = useCallback(async () => {
    try {
      const response = await jobsAPI.getDocuments(jobId, { document_type: 'client_po' });
      setClientPoDoc(response.data?.[0] || null);
    } catch {
      setClientPoDoc(null);
    }
  }, [jobId]);

  const fetchMargin = useCallback(async () => {
    try {
      const response = await jobsAPI.getMargin(jobId);
      setMargin(response.data);
    } catch {
      setMargin(null);
    }
  }, [jobId]);

  // Progress payments
  const [newPayment, setNewPayment] = useState({ amount: '', date: new Date().toISOString().slice(0, 10), note: '' });
  const handleAddProgressPayment = async () => {
    const amt = parseFloat(newPayment.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      await jobsAPI.addProgressPayment(jobId, {
        amount: amt,
        date: newPayment.date,
        note: newPayment.note,
        received: true,
      });
      toast.success('Progress payment recorded');
      setNewPayment({ amount: '', date: new Date().toISOString().slice(0, 10), note: '' });
      fetchJobData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    }
  };
  const handleToggleProgressPayment = async (pid, received) => {
    try {
      await jobsAPI.updateProgressPayment(jobId, pid, { received });
      fetchJobData();
    } catch {
      toast.error('Failed to update payment');
    }
  };
  const handleDeleteProgressPayment = async (pid) => {
    if (!window.confirm('Remove this progress payment?')) return;
    try {
      await jobsAPI.deleteProgressPayment(jobId, pid);
      toast.success('Removed');
      fetchJobData();
    } catch {
      toast.error('Failed to remove');
    }
  };

  useEffect(() => { fetchJobData(); fetchClientPoDoc(); fetchMargin(); }, [fetchJobData, fetchClientPoDoc, fetchMargin]);

  // Load the company-wide GST default once so new quotes inherit it.
  useEffect(() => {
    let cancelled = false;
    brandingAPI.get().then((res) => {
      if (cancelled) return;
      const flag = res?.data?.charge_gst;
      const def = typeof flag === 'boolean' ? flag : true;
      setCompanyChargesGst(def);
      setQuoteChargeGst(def);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (activeTab === 'documents') fetchDocuments(activeDocTab);
    else if (activeTab === 'activity') fetchActivity();
    else if (activeTab === 'emails') fetchEmails();
  }, [activeTab, activeDocTab, fetchDocuments, fetchActivity, fetchEmails]);

  // Upload handlers
  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Please select a file'); return; }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document_type', activeDocTab);
      formData.append('scope', 'job');
      formData.append('linked_entity_type', 'job');
      formData.append('linked_entity_id', jobId);
      if (uploadDescription) formData.append('description', uploadDescription);
      await documentsAPI.upload(formData);
      toast.success('Document uploaded');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadDescription('');
      fetchDocuments(activeDocTab);
    } catch (error) {
      toast.error('Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (doc) => {
    const link = document.createElement('a');
    link.href = `${process.env.REACT_APP_BACKEND_URL}${doc.file_path}`;
    link.download = doc.original_filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
    try {
      await documentsAPI.delete(doc.id);
      toast.success('Document deleted');
      fetchDocuments(activeDocTab);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Edit Job handlers
  const openEditJob = () => {
    setEditJobData({
      title: job?.title || '',
      description: job?.description || '',
      client_name: job?.client_name || '',
      client_email: job?.client_email || '',
      client_phone: job?.client_phone || '',
      customer_name: job?.customer_name || '',
      customer_email: job?.customer_email || '',
      customer_phone: job?.customer_phone || '',
      address: job?.address || '',
      start_date: job?.start_date || '',
      end_date: job?.end_date || '',
      budget: job?.budget || '',
      is_roof: !!job?.is_roof,
      client_prefix: job?.client_prefix || ''
    });
    setEditJobOpen(true);
  };

  const handleUpdateJob = async () => {
    try {
      setEditJobLoading(true);
      // Sanitize: strip empty strings so Pydantic Optional[float]/Optional[str] validation passes.
      // (Empty strings can't be coerced to float, so `budget: ""` would 422 the whole request.)
      const clean = {};
      Object.entries(editJobData).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        clean[k] = k === 'budget' ? parseFloat(v) : v;
      });
      // Guard against NaN on budget after coercion
      if ('budget' in clean && Number.isNaN(clean.budget)) delete clean.budget;
      // Normalize client_prefix — server also sanitizes but doing it up-front
      // keeps the user's stored value tidy (uppercase, alphanumeric, ≤6 chars).
      if ('client_prefix' in clean) {
        clean.client_prefix = String(clean.client_prefix).replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6);
        if (!clean.client_prefix) delete clean.client_prefix;
      }
      await jobsAPI.update(jobId, clean);
      toast.success('Job updated successfully');
      setEditJobOpen(false);
      fetchJobData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(', ')
        : (detail || error?.message || 'Failed to update job');
      toast.error(msg);
    } finally {
      setEditJobLoading(false);
    }
  };

  // Send Client Portal Link handlers
  const getClientPortalLink = () => {
    // Generate a link based on the first quote's client token, or a generic job link
    const quote = quotes?.[0];
    if (quote?.client_token) {
      return `${window.location.origin}/client/quote/${quote.client_token}`;
    }
    return null;
  };

  const handleCopyLink = () => {
    const link = getClientPortalLink();
    if (link) {
      navigator.clipboard.writeText(link);
      toast.success('Link copied to clipboard');
    } else {
      toast.error('No client portal link available. Create a quote first.');
    }
  };

  const handleSendPortalLink = async () => {
    if (!job?.client_email) {
      toast.error('No client email address');
      return;
    }
    const link = getClientPortalLink();
    if (!link) {
      toast.error('No client portal link available. Create a quote first.');
      return;
    }
    try {
      setSendLinkLoading(true);
      await emailAPI.send({
        recipient_email: job.client_email,
        subject: `Your Project Portal - ${job.title}`,
        body: `Dear ${job.client_name},\n\nYou can view your project details and quotes using the link below:\n\n${link}\n\nBest regards`,
        job_id: jobId
      });
      toast.success('Portal link sent to client');
      setSendLinkOpen(false);
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendLinkLoading(false);
    }
  };

  // Email handling
  const handleSendEmail = async () => {
    if (!emailData.recipient) { toast.error('Recipient email required'); return; }
    try {
      setEmailSending(true);
      
      // If we have file uploads, we need to upload them first, then send email
      const formData = new FormData();
      formData.append('template_type', emailData.template || '');
      formData.append('recipient_email', emailData.recipient);
      formData.append('subject', emailData.subject);
      formData.append('body', emailData.body);
      formData.append('custom_message', emailData.customMessage);
      formData.append('attachment_ids', JSON.stringify(emailAttachments));
      
      // Add uploaded files
      emailFileUploads.forEach((file, index) => {
        formData.append(`files`, file);
      });
      
      await jobsAPI.sendEmailWithAttachments(jobId, formData);
      
      toast.success(`Email sent${(emailAttachments.length + emailFileUploads.length) > 0 ? ` with ${emailAttachments.length + emailFileUploads.length} attachment(s)` : ''}`);
      setEmailOpen(false);
      setEmailData({ template: '', recipient: '', subject: '', body: '', customMessage: '' });
      setEmailAttachments([]);
      setEmailFileUploads([]);
      fetchEmails();
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    setEmailFileUploads(prev => [...prev, ...files]);
  };

  const removeUploadedFile = (index) => {
    setEmailFileUploads(prev => prev.filter((_, i) => i !== index));
  };

  const fetchAvailableAttachments = async () => {
    try {
      const response = await emailAPI.getDocumentsForEmail({ job_id: jobId });
      setAvailableAttachments(response.data || []);
    } catch (error) {
      setAvailableAttachments([]);
    }
  };

  const handleOpenAttachments = () => {
    fetchAvailableAttachments();
    setAttachmentsDialogOpen(true);
  };

  const handleTemplateChange = (template) => {
    if (template && emailTemplates[template] && jobData?.job) {
      const t = emailTemplates[template];
      let subject = t.subject.replace('{job_title}', jobData.job.title).replace('{job_address}', jobData.job.address);
      let body = t.body.replace('{job_title}', jobData.job.title).replace('{job_address}', jobData.job.address).replace('{client_name}', jobData.job.client_name);
      setEmailData({ ...emailData, template, subject, body, recipient: jobData.job.client_email || '' });
    } else {
      setEmailData({ ...emailData, template, subject: `Re: ${jobData?.job?.title} - ${jobData?.job?.address}`, body: '' });
    }
  };

  // Quote section handling
  const addSection = () => {
    setQuoteSectionsData([...quoteSectionsData, { section_type: 'custom', section_name: '', items: [{ description: '', quantity: 1, unit: 'Item', unit_price: 0, trade_type: 'General' }] }]);
  };

  const removeSection = (sectionIdx) => {
    if (quoteSectionsData.length <= 1) return;
    setQuoteSectionsData(quoteSectionsData.filter((_, i) => i !== sectionIdx));
  };

  // Clone a section + its items into a fresh entry right below the original.
  // Prices and qty are kept verbatim — the user typically just renames "Bed 1" → "Bed 2".
  const duplicateSection = (sectionIdx) => {
    const src = quoteSectionsData[sectionIdx];
    if (!src) return;
    const clone = {
      section_type: src.section_type || 'custom',
      section_name: src.section_name ? `${src.section_name} (copy)` : '',
      items: (src.items || []).map((it) => ({
        description: it.description || '',
        quantity: it.quantity || 1,
        unit: it.unit || 'Item',
        unit_price: it.unit_price || 0,
        trade_type: it.trade_type || 'General',
      })),
    };
    const next = [...quoteSectionsData];
    next.splice(sectionIdx + 1, 0, clone);
    setQuoteSectionsData(next);
    toast.success('Section duplicated — rename as needed');
  };

  // Reorder sections via drag-and-drop (dnd-kit). We tag each section with a
  // stable `_dnd_id` on load so keys survive reordering.
  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const withIds = quoteSectionsData.map((s) => (s._dnd_id ? s : { ...s, _dnd_id: `sec-${Math.random().toString(36).slice(2, 9)}` }));
    const oldIdx = withIds.findIndex((s) => s._dnd_id === active.id);
    const newIdx = withIds.findIndex((s) => s._dnd_id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    setQuoteSectionsData(arrayMove(withIds, oldIdx, newIdx));
  };

  // Ensure every section carries a stable dnd id — call whenever we render.
  const sectionsWithDndIds = quoteSectionsData.map((s, i) =>
    s._dnd_id ? s : { ...s, _dnd_id: `sec-${i}-${(s.section_type || 'x')}-${(s.section_name || '').slice(0, 4)}` }
  );

  // Open the "Import from another quote" picker (lazy-fetch the tenant's quotes).
  const openImportFromQuote = async () => {
    setImportFromQuoteOpen(true);
    setImportQuoteSearch('');
    try {
      const res = await quotesAPI.getAll();
      // Hide quotes from this job — the user is editing one of them already.
      const list = (res.data || []).filter((q) => q.job_id !== jobId);
      setAllTenantQuotes(list);
    } catch (error) {
      toast.error('Failed to load existing quotes');
    }
  };

  // Pull all sections (and their items, with prices intact) from the chosen
  // source quote and APPEND them to whatever is already in the dialog.
  const handleImportFromQuote = (sourceQuote) => {
    const incoming = [];
    if (sourceQuote.sections && sourceQuote.sections.length > 0) {
      sourceQuote.sections.forEach((section) => {
        incoming.push({
          section_type: section.section_type || 'custom',
          section_name: section.section_name || 'Imported section',
          items: (section.items || []).map((it) => ({
            description: it.description || '',
            quantity: it.quantity || 1,
            unit: it.unit || 'Item',
            unit_price: it.unit_price || 0,
            trade_type: it.trade_type || 'General',
          })),
        });
      });
    } else if (sourceQuote.items && sourceQuote.items.length > 0) {
      // Older quotes stored flat items only — wrap them in a single section.
      incoming.push({
        section_type: 'imported',
        section_name: `Imported from ${sourceQuote.quote_number}`,
        items: sourceQuote.items.map((it) => ({
          description: it.description || '',
          quantity: it.quantity || 1,
          unit: it.unit || 'Item',
          unit_price: it.unit_price || 0,
          trade_type: it.trade_type || 'General',
        })),
      });
    }
    if (incoming.length === 0) {
      toast.error('That quote has no line items to import');
      return;
    }
    // If the dialog only contains the default empty section, replace it. Otherwise append.
    const startsEmpty = quoteSectionsData.length === 1
      && (!quoteSectionsData[0].section_name)
      && quoteSectionsData[0].items.length === 1
      && !quoteSectionsData[0].items[0].description
      && !quoteSectionsData[0].items[0].unit_price;
    setQuoteSectionsData(startsEmpty ? incoming : [...quoteSectionsData, ...incoming]);
    setImportFromQuoteOpen(false);
    toast.success(`Imported ${incoming.length} section${incoming.length === 1 ? '' : 's'} from ${sourceQuote.quote_number}`);
  };

  const updateSection = (sectionIdx, field, value) => {
    const updated = [...quoteSectionsData];
    updated[sectionIdx][field] = value;
    setQuoteSectionsData(updated);
  };

  const addItemToSection = (sectionIdx) => {
    const updated = [...quoteSectionsData];
    updated[sectionIdx].items.push({ description: '', quantity: 1, unit: 'Item', unit_price: 0, trade_type: 'General' });
    setQuoteSectionsData(updated);
  };

  const removeItemFromSection = (sectionIdx, itemIdx) => {
    const updated = [...quoteSectionsData];
    if (updated[sectionIdx].items.length <= 1) return;
    updated[sectionIdx].items = updated[sectionIdx].items.filter((_, i) => i !== itemIdx);
    setQuoteSectionsData(updated);
  };

  const updateSectionItem = (sectionIdx, itemIdx, field, value) => {
    const updated = [...quoteSectionsData];
    updated[sectionIdx].items[itemIdx][field] = value;
    setQuoteSectionsData(updated);
  };

  const handleAddUnitType = async (unit) => {
    try {
      await tradeAPI.addUnitType(unit);
      // Add to local state
      if (!unitTypes.includes(unit)) {
        setUnitTypes([...unitTypes, unit]);
      }
      toast.success(`Unit "${unit}" added`);
    } catch (error) {
      // Still add to local state even if API fails
      if (!unitTypes.includes(unit)) {
        setUnitTypes([...unitTypes, unit]);
      }
    }
  };

  const calculateQuoteTotal = () => {
    let subtotal = 0;
    quoteSectionsData.forEach(section => {
      section.items.forEach(item => {
        subtotal += (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      });
    });
    const tax = quoteChargeGst ? subtotal * 0.1 : 0;
    return { subtotal, tax, total: subtotal + tax };
  };

  const handleCreateQuote = async () => {
    // Flatten sections to items for API
    const allItems = [];
    quoteSectionsData.forEach(section => {
      section.items.forEach(item => {
        if (item.description) {
          allItems.push({
            ...item,
            section: section.section_type,
            section_name: section.section_name || sectionLabels[section.section_type]
          });
        }
      });
    });

    if (allItems.length === 0) { toast.error('Add at least one item'); return; }

    try {
      if (editingQuoteId) {
        // Update existing quote
        await quotesAPI.update(editingQuoteId, {
          sections: quoteSectionsData,
          items: allItems,
          payment_terms: quotePaymentTerms,
          payment_details: quotePaymentDetails,
          charge_gst: quoteChargeGst
        });
        toast.success('Quote updated');
      } else {
        // Create new quote
        await quotesAPI.create({
          job_id: jobId,
          client_name: jobData.job.client_name,
          client_email: jobData.job.client_email,
          sections: quoteSectionsData,
          items: allItems,
          payment_terms: quotePaymentTerms,
          payment_details: quotePaymentDetails,
          charge_gst: quoteChargeGst
        });
        toast.success('Quote created');
      }
      setQuoteOpen(false);
      setEditingQuoteId(null);
      setQuoteSectionsData([{ section_type: 'preliminaries', section_name: '', items: [{ description: '', quantity: 1, unit: 'Item', unit_price: 0, trade_type: 'General' }] }]);
      setQuotePaymentTerms('');
      setQuotePaymentDetails('');
      setQuoteChargeGst(companyChargesGst);
      fetchJobData();
    } catch (error) {
      toast.error(editingQuoteId ? 'Failed to update quote' : 'Failed to create quote');
    }
  };

  // View quote
  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setViewQuoteOpen(true);
  };

  // Edit quote
  const handleEditQuote = (quote) => {
    setEditingQuoteId(quote.id);
    // Load quote data into the form
    if (quote.sections && quote.sections.length > 0) {
      setQuoteSectionsData(quote.sections.map(section => ({
        section_type: section.section_type || 'general',
        section_name: section.section_name || '',
        items: (section.items || []).map(item => ({
          description: item.description || '',
          quantity: item.quantity || 1,
          unit: item.unit || 'Item',
          unit_price: item.unit_price || 0,
          trade_type: item.trade_type || 'General'
        }))
      })));
    } else if (quote.items && quote.items.length > 0) {
      setQuoteSectionsData([{
        section_type: 'general',
        section_name: 'Items',
        items: quote.items.map(item => ({
          description: item.description || '',
          quantity: item.quantity || 1,
          unit: item.unit || 'Item',
          unit_price: item.unit_price || 0,
          trade_type: item.trade_type || 'General'
        }))
      }]);
    }
    setQuotePaymentTerms(quote.payment_terms || '');
    setQuotePaymentDetails(quote.payment_details || '');
    setQuoteChargeGst(quote.charge_gst !== false);
    setQuoteOpen(true);
  };

  // View invoice / Generate Final Invoice / GST toggle / Delete invoice
  // are all owned by the <InvoicesTab/> component now.

  // Duplicate quote
  const handleDuplicateQuote = async (quoteId) => {
    try {
      const response = await quotesAPI.duplicate(quoteId);
      toast.success(`Quote duplicated! New quote: ${response.data.quote_number}`);
      fetchJobData(); // Refresh to show new quote
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate quote');
    }
  };

  // Delete quote
  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      await quotesAPI.delete(quoteId);
      toast.success('Quote deleted');
      fetchJobData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quote');
    }
  };

  // Inline GST toggle on a quote card — persists immediately (no need to re-open the edit dialog).
  const handleToggleQuoteGst = async (quote, nextChargeGst) => {
    try {
      await quotesAPI.update(quote.id, { charge_gst: nextChargeGst });
      toast.success(`GST ${nextChargeGst ? 'enabled' : 'disabled'} on ${quote.quote_number}`);
      fetchJobData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update quote');
    }
  };

  // Import quote from PDF
  const handleImportPdf = async () => {
    if (!importPdfFile) {
      toast.error('Please select a PDF file');
      return;
    }

    setImportPdfLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importPdfFile);
      formData.append('job_id', jobId);

      const response = await aiAPI.importQuotePdf(formData);
      
      if (response.data.status === 'success') {
        // Check if we have sections (new format) or just items (old format)
        const extractedSections = response.data.sections || [];
        const extractedItems = response.data.items || [];
        
        if (extractedSections.length > 0) {
          // New format with sections - use them directly
          const formattedSections = extractedSections.map(section => ({
            section_type: section.section_type || 'custom',
            section_name: section.section_name || 'Imported Section',
            items: (section.items || []).map(item => ({
              description: item.description || '',
              quantity: item.quantity || 1,
              unit: item.unit || 'Item',
              unit_price: item.unit_price || 0,
              trade_type: item.trade_type || 'General'
            }))
          }));
          setQuoteSectionsData(formattedSections);
          toast.success(`Extracted ${extractedSections.length} section(s) from PDF!`);
        } else if (extractedItems.length > 0) {
          // Old format - wrap in single section
          setQuoteSectionsData([{
            section_type: 'imported',
            section_name: 'Imported from PDF',
            items: extractedItems.map(item => ({
              description: item.description || '',
              quantity: item.quantity || 1,
              unit: item.unit || 'Item',
              unit_price: item.unit_price || 0,
              trade_type: item.trade_type || 'General'
            }))
          }]);
          toast.success('Quote data extracted! Review and save.');
        }
        
        setImportPdfOpen(false);
        setImportPdfFile(null);
        setQuoteOpen(true);
      } else {
        toast.info(response.data.message || 'PDF processed with partial results');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import PDF');
    } finally {
      setImportPdfLoading(false);
    }
  };

  // Download PDF
  const handleDownloadPdf = async (id, type = 'quote') => {
    try {
      let response;
      if (type === 'quote') response = await quotesAPI.downloadPdf(id);
      else if (type === 'po') response = await purchaseOrdersAPI.downloadPdf(id);
      else response = await invoicesAPI.downloadPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // PO from Quote
  const openPODialog = (quote) => { 
    // Flatten items from sections if they exist
    let allItems = [];
    if (quote.sections && quote.sections.length > 0) {
      quote.sections.forEach((section, sIdx) => {
        (section.items || []).forEach((item, iIdx) => {
          allItems.push({
            ...item,
            section_name: section.section_name || section.section_type,
            original_index: `${sIdx}-${iIdx}`,
            itemKey: `${sIdx}-${iIdx}` // Unique key for tracking
          });
        });
      });
    } else {
      allItems = (quote.items || []).map((item, idx) => ({ 
        ...item, 
        original_index: idx,
        itemKey: `item-${idx}`
      }));
    }
    
    setPoQuote({ ...quote, flatItems: allItems }); 
    setPoOpen(true); 
    setPoContractor(''); 
    setPoTradeFilter('all'); 
    setAdditionalItems([]);
    setItemAllocations([]);
    setSelectedItems({});
  };

  // Toggle item selection for PO
  const toggleItemSelection = (itemIndex) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemIndex]: prev[itemIndex] ? undefined : true
    }));
  };

  // Get allocated quantity for an item
  const getAllocatedQty = (itemKey) => {
    return itemAllocations
      .filter(a => a.itemKey === itemKey)
      .reduce((sum, a) => sum + (a.quantity || 0), 0);
  };

  // Get remaining quantity for an item
  const getRemainingQty = (item) => {
    const allocated = getAllocatedQty(item.itemKey);
    return (item.quantity || 1) - allocated;
  };

  // Add allocation for an item to a contractor
  const addItemAllocation = (item, remainingQty) => {
    if (!poContractor) {
      toast.error('Select a contractor first');
      return;
    }
    
    if (remainingQty <= 0) {
      toast.error('Item fully allocated');
      return;
    }
    
    const contractor = contractors.find(c => c.id === poContractor);
    
    // Check if same item already allocated to same contractor - if so, just add to quantity
    const existingIdx = itemAllocations.findIndex(
      a => a.itemKey === item.itemKey && a.contractor_id === poContractor
    );
    
    if (existingIdx >= 0) {
      // Add to existing allocation
      setItemAllocations(prev => {
        const updated = [...prev];
        updated[existingIdx] = {
          ...updated[existingIdx],
          quantity: updated[existingIdx].quantity + remainingQty
        };
        return updated;
      });
      toast.success(`Added ${remainingQty} more to ${contractor?.name}`);
    } else {
      // New allocation
      setItemAllocations(prev => [...prev, {
        itemKey: item.itemKey,
        contractor_id: poContractor,
        contractor_name: contractor?.name || 'Unknown',
        description: item.description,
        trade_type: item.trade_type || 'General',
        unit: item.unit || 'Item',
        quantity: remainingQty,
        unit_price: item.unit_price || 0,
        original_quantity: item.quantity || 1,
        original_price: item.unit_price || 0
      }]);
      toast.success(`Added to ${contractor?.name}`);
    }
  };

  // Update allocation quantity/price
  const updateAllocation = (index, field, value) => {
    setItemAllocations(prev => {
      const updated = [...prev];
      const numericFields = new Set(['quantity', 'unit_price']);
      updated[index] = {
        ...updated[index],
        [field]: numericFields.has(field) ? (parseFloat(value) || 0) : value,
      };
      return updated;
    });
  };

  // Remove allocation
  const removeAllocation = (index) => {
    setItemAllocations(prev => prev.filter((_, i) => i !== index));
  };

  // Generate POs from allocations
  const handleGeneratePO = async () => {
    if (itemAllocations.length === 0) {
      toast.error('Add at least one item allocation');
      return;
    }
    
    // Group allocations by contractor
    const byContractor = {};
    itemAllocations.forEach(alloc => {
      if (!byContractor[alloc.contractor_id]) {
        byContractor[alloc.contractor_id] = {
          contractor_id: alloc.contractor_id,
          contractor_name: alloc.contractor_name,
          items: []
        };
      }
      byContractor[alloc.contractor_id].items.push({
        description: alloc.description,
        trade_type: alloc.trade_type,
        unit: alloc.unit || 'Item',
        quantity: alloc.quantity,
        unit_price: alloc.unit_price
      });
    });
    
    // Add additional items to first contractor or selected contractor
    if (additionalItems.length > 0 && poContractor) {
      if (!byContractor[poContractor]) {
        const contractor = contractors.find(c => c.id === poContractor);
        byContractor[poContractor] = {
          contractor_id: poContractor,
          contractor_name: contractor?.name || 'Unknown',
          items: []
        };
      }
      additionalItems.forEach(item => {
        byContractor[poContractor].items.push({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price
        });
      });
    }
    
    // Create POs for each contractor
    try {
      const contractorGroups = Object.values(byContractor);
      for (const group of contractorGroups) {
        await quotesAPI.generatePO(poQuote.id, {
          contractor_id: group.contractor_id,
          custom_items: group.items,
          additional_items: []
        });
      }
      toast.success(`Created ${contractorGroups.length} PO(s)`);
      setPoOpen(false);
      fetchJobData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO');
    }
  };

  const addAdditionalItem = (item) => {
    setAdditionalItems([...additionalItems, { ...item, quantity: 1 }]);
  };

  // Calculate totals by contractor with trade grouping
  const getAllocationTotals = () => {
    const totals = {};
    itemAllocations.forEach(alloc => {
      if (!totals[alloc.contractor_id]) {
        totals[alloc.contractor_id] = { 
          name: alloc.contractor_name, 
          total: 0, 
          items: 0,
          trades: new Set()
        };
      }
      totals[alloc.contractor_id].total += (alloc.quantity * alloc.unit_price);
      totals[alloc.contractor_id].items += 1;
      totals[alloc.contractor_id].trades.add(alloc.trade_type);
    });
    return totals;
  };

  // Calculate budget summary
  const getBudgetSummary = () => {
    const items = poQuote?.flatItems || poQuote?.items || [];
    const quoteTotal = items.reduce((sum, item) => sum + (parseFloat(item.quantity || 1) * parseFloat(item.unit_price || 0)), 0);
    const allocatedTotal = itemAllocations.reduce((sum, a) => sum + (a.quantity * a.unit_price), 0);
    const unallocatedTotal = quoteTotal - allocatedTotal;
    
    // Count unallocated items
    const unallocatedItems = items.filter(item => getRemainingQty(item) > 0).length;
    const totalItems = items.length;
    const allocatedItems = totalItems - unallocatedItems;
    
    return {
      quoteTotal,
      allocatedTotal,
      unallocatedTotal,
      margin: quoteTotal > 0 ? ((quoteTotal - allocatedTotal) / quoteTotal * 100).toFixed(1) : 0,
      unallocatedItems,
      allocatedItems,
      totalItems,
      isFullyAllocated: unallocatedItems === 0
    };
  };

  // Cancel PO
  const handleCancelPO = async () => {
    if (!cancellingPo) return;
    try {
      await purchaseOrdersAPI.cancel(cancellingPo.id, {
        reason: cancelReason,
        send_email: sendCancelEmail
      });
      toast.success('PO cancelled');
      setCancelPoOpen(false);
      setCancellingPo(null);
      setCancelReason('');
      fetchJobData();
    } catch (error) {
      toast.error('Failed to cancel PO');
    }
  };

  // Edit PO
  const handleEditPO = (po) => {
    setEditingPo(po);
    setEditPoItems(po.items?.map(item => ({
      description: item.description || '',
      quantity: item.quantity || 1,
      unit_price: item.unit_price || 0
    })) || [{ description: '', quantity: 1, unit_price: 0 }]);
    setEditPoOpen(true);
  };

  const handleUpdatePO = async () => {
    if (!editingPo) return;
    try {
      const subtotal = editPoItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
      await purchaseOrdersAPI.update(editingPo.id, {
        items: editPoItems,
        subtotal: subtotal,
        tax: subtotal * 0.1,
        total: subtotal * 1.1
      });
      toast.success('PO updated');
      setEditPoOpen(false);
      setEditingPo(null);
      fetchJobData();
    } catch (error) {
      toast.error('Failed to update PO');
    }
  };

  // Send PO
  const handleSendPO = (po) => {
    // Look up the contractor so we can show their email (POs only store contractor_id/_name)
    const contractor = (contractors || []).find((c) => c.id === po.contractor_id);
    setSendingPo({
      ...po,
      contractor_email: po.contractor_email || contractor?.email || '',
      contractor_name: po.contractor_name || contractor?.name || 'Contractor',
    });
    setPoEmailMessage(`Please find attached Purchase Order ${po.po_number} for your review.\n\nPlease confirm receipt and expected delivery timeline.`);
    setSendPoOpen(true);
  };

  const handleSendPOEmail = async () => {
    if (!sendingPo) return;
    setPoSending(true);
    try {
      await purchaseOrdersAPI.send(sendingPo.id, {
        message: poEmailMessage
      });
      toast.success('PO sent to contractor');
      setSendPoOpen(false);
      setSendingPo(null);
      setPoEmailMessage('');
      fetchJobData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send PO');
    } finally {
      setPoSending(false);
    }
  };

  // AI: Suggest Trades for Job
  const handleAISuggestTrades = async () => {
    setAiSuggestLoading(true);
    try {
      const response = await aiAPI.suggestTrades(jobId);
      setAiTradeSuggestions(response.data);
      setAiSuggestOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get AI suggestions');
    } finally {
      setAiSuggestLoading(false);
    }
  };

  // AI: Get Cost Estimate
  const handleAICostEstimate = async () => {
    setAiCostLoading(true);
    try {
      const response = await aiAPI.estimateCost({
        job_description: `${job?.title || ''} - ${job?.description || ''}`,
        job_type: job?.job_type || 'renovation'
      });
      setAiCostEstimate(response.data);
      setAiCostOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get cost estimate');
    } finally {
      setAiCostLoading(false);
    }
  };

  // AI: Draft Email
  const handleAIDraftEmail = async () => {
    if (!aiEmailRecipient) {
      toast.error('Please enter recipient name');
      return;
    }
    setAiEmailLoading(true);
    try {
      const response = await aiAPI.draftEmail({
        email_type: aiEmailType,
        recipient_name: aiEmailRecipient,
        context: {
          job_title: job?.title || '',
          job_address: job?.address || '',
          client_name: job?.client_name || '',
          quote_amount: quotes?.[0]?.total || 0
        }
      });
      setAiEmailDraft(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to draft email');
    } finally {
      setAiEmailLoading(false);
    }
  };

  const applyAIEmailDraft = () => {
    if (aiEmailDraft) {
      setEmailData({
        ...emailData,
        subject: aiEmailDraft.subject,
        body: aiEmailDraft.body,
        recipient: job?.client_email || ''
      });
      setAiEmailOpen(false);
      setEmailOpen(true);
      toast.success('AI draft applied!');
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  if (!jobData) return <div className="text-center py-12"><h2 className="text-xl text-muted-foreground">Job not found</h2><Button variant="link" onClick={() => navigate('/jobs')}>Back to Jobs</Button></div>;

  const { job, quotes, invoices, purchase_orders } = jobData;
  const StatusIcon = statusConfig[job.status]?.icon || Clock;
  const filteredPOItems = poQuote?.items?.filter(item => poTradeFilter === 'all' || item.trade_type === poTradeFilter) || [];
  const uniqueTrades = [...new Set(poQuote?.items?.map(item => item.trade_type).filter(Boolean))];
  const totals = calculateQuoteTotal();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')}><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{job.title}</h1>
          <p className="text-muted-foreground">{job.address}</p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${statusConfig[job.status]?.color}`}>
          <StatusIcon className="h-4 w-4" />
          <span className="text-sm font-medium">{statusConfig[job.status]?.label}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setQuoteOpen(true)} size="sm"><Plus className="h-4 w-4 mr-1" /> New Quote</Button>
        <Button onClick={() => setEmailOpen(true)} variant="outline" size="sm"><Mail className="h-4 w-4 mr-1" /> Send Email</Button>
        <div className="border-l border-border mx-2"></div>
        <Button 
          onClick={handleAISuggestTrades} 
          variant="outline" 
          size="sm" 
          disabled={aiSuggestLoading}
          className="border-primary/50 hover:border-primary"
        >
          {aiSuggestLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserCheck className="h-4 w-4 mr-1 text-primary" />}
          AI Trade Match
        </Button>
        <Button 
          onClick={handleAICostEstimate} 
          variant="outline" 
          size="sm" 
          disabled={aiCostLoading}
          className="border-primary/50 hover:border-primary"
        >
          {aiCostLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Brain className="h-4 w-4 mr-1 text-primary" />}
          AI Cost Estimate
        </Button>
        <Button 
          onClick={() => { setAiEmailOpen(true); setAiEmailRecipient(job.client_name || ''); }}
          variant="outline" 
          size="sm"
          className="border-primary/50 hover:border-primary"
        >
          <Wand2 className="h-4 w-4 mr-1 text-primary" />
          AI Draft Email
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="quotes">Quotes ({quotes.length})</TabsTrigger>
          <TabsTrigger value="pos">POs ({purchase_orders.length})</TabsTrigger>
          <TabsTrigger value="invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="emails">Emails</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {job.imported_from_po && (job.imported_from_po.po_number || job.imported_from_po.po_reference || job.imported_from_po.client_abn) && (
            <Card className="bg-primary/5 border-primary/30" data-testid="imported-from-po-banner">
              <CardContent className="p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="flex items-center gap-2 font-medium text-primary">
                  <FileInput className="h-5 w-5" />
                  Imported from Client PO
                </div>
                {job.imported_from_po.po_number && (
                  <div className="text-sm"><span className="text-muted-foreground">PO #:</span> <span className="font-medium ml-1" data-testid="banner-po-number">{job.imported_from_po.po_number}</span></div>
                )}
                {job.imported_from_po.po_reference && (
                  <div className="text-sm"><span className="text-muted-foreground">Ref:</span> <span className="font-medium ml-1">{job.imported_from_po.po_reference}</span></div>
                )}
                {job.imported_from_po.po_date && (
                  <div className="text-sm"><span className="text-muted-foreground">Date:</span> <span className="font-medium ml-1">{formatDate(job.imported_from_po.po_date)}</span></div>
                )}
                {job.imported_from_po.client_abn && (
                  <div className="text-sm"><span className="text-muted-foreground">Client ABN:</span> <span className="font-medium ml-1">{job.imported_from_po.client_abn}</span></div>
                )}
                {clientPoDoc && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-auto"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${clientPoDoc.file_path}`, '_blank')}
                    data-testid="view-original-po-btn"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> View Original PO
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
          {margin && (margin.invoice_count > 0 || margin.po_count > 0) && (
            <Card className="bg-card border-border" data-testid="job-margin-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Job Margin</div>
                  <div className={`text-xs font-medium px-2 py-0.5 rounded-sm ${margin.margin_pct >= 20 ? 'bg-green-500/20 text-green-400' : margin.margin_pct >= 10 ? 'bg-yellow-500/20 text-yellow-400' : margin.margin_pct >= 0 ? 'bg-orange-500/20 text-orange-400' : 'bg-red-500/20 text-red-400'}`}>
                    {margin.margin_pct}% margin
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground">Revenue (Invoiced)</div>
                    <div className="text-2xl font-bold text-primary" data-testid="margin-revenue">${margin.revenue.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{margin.invoice_count} invoice(s)</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Costs (Sub POs)</div>
                    <div className="text-2xl font-bold" data-testid="margin-costs">${margin.costs.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{margin.po_count} PO(s)</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Margin</div>
                    <div className={`text-2xl font-bold ${margin.margin >= 0 ? 'text-green-400' : 'text-destructive'}`} data-testid="margin-value">${margin.margin.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{margin.margin >= 0 ? 'Profitable' : 'Loss'}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <PoBudgetBurndown job={job} invoices={invoices} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="bg-card border-border lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Job Details</CardTitle>
                <Button size="sm" variant="outline" onClick={openEditJob} data-testid="edit-job-btn">
                  <Pencil className="h-4 w-4 mr-1" /> Edit
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {job.description && <div><label className="text-sm text-muted-foreground">Description</label><p className="mt-1">{job.description}</p></div>}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm text-muted-foreground">Address</p><p>{job.address}</p></div></div>
                  <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm text-muted-foreground">Budget</p><p>${job.budget?.toLocaleString() || 'N/A'}</p></div></div>
                  <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm text-muted-foreground">Start Date</p><p>{formatDate(job.start_date)}</p></div></div>
                  <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm text-muted-foreground">End Date</p><p>{formatDate(job.end_date)}</p></div></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">
                  Client <span className="text-xs font-normal text-muted-foreground">(invoiced)</span>
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => setSendLinkOpen(true)} data-testid="send-link-btn">
                  <Link className="h-4 w-4 mr-1" /> Send Link
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" /><span>{job.client_name}</span></div>
                {job.client_email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /><a href={`mailto:${job.client_email}`} className="text-primary hover:underline">{job.client_email}</a></div>}
                {job.client_phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><span>{job.client_phone}</span></div>}
                {(job.customer_name || job.customer_email || job.customer_phone) && (
                  <div className="pt-3 mt-3 border-t border-border/60" data-testid="job-customer-block">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      Customer <span className="font-normal normal-case">(site contact — not billed)</span>
                    </p>
                    {job.customer_name && <div className="flex items-center gap-2 text-sm"><Users className="h-4 w-4 text-muted-foreground" /><span>{job.customer_name}</span></div>}
                    {job.customer_email && <div className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-muted-foreground" /><a href={`mailto:${job.customer_email}`} className="text-primary hover:underline">{job.customer_email}</a></div>}
                    {job.customer_phone && <div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-muted-foreground" /><span>{job.customer_phone}</span></div>}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-card border-border"><CardContent className="p-4"><p className="text-sm text-muted-foreground">Quotes</p><p className="text-2xl font-bold">{quotes.length}</p></CardContent></Card>
            <Card className="bg-card border-border"><CardContent className="p-4"><p className="text-sm text-muted-foreground">Invoices</p><p className="text-2xl font-bold">{invoices.length}</p></CardContent></Card>
            <Card className="bg-card border-border"><CardContent className="p-4"><p className="text-sm text-muted-foreground">Purchase Orders</p><p className="text-2xl font-bold">{purchase_orders.length}</p></CardContent></Card>
            <Card className="bg-card border-border"><CardContent className="p-4"><p className="text-sm text-muted-foreground">Contractors</p><p className="text-2xl font-bold">{jobData.contractors?.length || 0}</p></CardContent></Card>
          </div>

          {/* Progress Payments */}
          <Card className="bg-card border-border" data-testid="progress-payments-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2"><DollarSign className="h-5 w-5 text-green-400" /> Progress Payments</span>
                {(() => {
                  const list = job.progress_payments || [];
                  const received = list.filter(p => p.received).reduce((s, p) => s + (p.amount || 0), 0);
                  const total = list.reduce((s, p) => s + (p.amount || 0), 0);
                  return (
                    <span className="text-sm font-mono text-muted-foreground">
                      ${received.toLocaleString(undefined, { minimumFractionDigits: 2 })} received{total !== received ? ` of $${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ''}
                    </span>
                  );
                })()}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(job.progress_payments || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No progress payments recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {(job.progress_payments || []).map((p) => (
                    <div key={p.id} className="flex items-center gap-3 p-2 rounded-sm border border-border bg-input/40" data-testid={`progress-payment-row-${p.id}`}>
                      <input
                        type="checkbox"
                        checked={!!p.received}
                        onChange={(e) => handleToggleProgressPayment(p.id, e.target.checked)}
                        className="h-4 w-4 accent-green-500"
                        data-testid={`progress-payment-checkbox-${p.id}`}
                        title="Mark as received"
                      />
                      <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                        <div className="font-mono font-bold">${(p.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                        <div className="text-muted-foreground">{p.date}</div>
                        <div className="truncate" title={p.note}>{p.note || <span className="text-muted-foreground italic">no note</span>}</div>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => handleDeleteProgressPayment(p.id)} className="h-7 w-7 text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pt-2 border-t border-border/60">
                <Input
                  type="number" step="0.01" placeholder="Amount"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                  data-testid="progress-payment-amount"
                />
                <DatePicker
                  value={newPayment.date}
                  onChange={(d) => setNewPayment({ ...newPayment, date: d })}
                  placeholder="Payment date"
                  data-testid="progress-payment-date"
                />
                <Input
                  placeholder="Note (optional)"
                  value={newPayment.note}
                  onChange={(e) => setNewPayment({ ...newPayment, note: e.target.value })}
                  className="sm:col-span-1"
                  data-testid="progress-payment-note"
                />
                <Button onClick={handleAddProgressPayment} data-testid="progress-payment-add-btn">
                  <Plus className="h-4 w-4 mr-1" /> Record
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quotes Tab */}
        <TabsContent value="quotes">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Quotes</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/quotes/import-pdf?job_id=${jobId}`)} data-testid="open-import-pdf-wizard"><Upload className="h-4 w-4 mr-1" /> Import PDF</Button>
                <Button size="sm" onClick={() => setQuoteOpen(true)}><Plus className="h-4 w-4 mr-1" /> New Quote</Button>
              </div>
            </CardHeader>
            <CardContent>
              {quotes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground"><FileText className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No quotes yet</p></div>
              ) : (
                <div className="space-y-3">
                  {quotes.map(q => (
                    <div key={q.id} className="border border-border rounded-lg p-4 hover:bg-secondary/20" data-testid={`quote-card-${q.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold flex items-center gap-2 flex-wrap">
                            {q.quote_number}
                            <span className={`text-xs px-2 py-0.5 rounded ${q.charge_gst === false ? 'bg-orange-500/20 text-orange-400' : 'bg-green-500/20 text-green-400'}`}>
                              {q.charge_gst === false ? 'GST OFF' : 'GST ON'}
                            </span>
                          </h4>
                          <p className="text-sm text-muted-foreground">{q.items?.length || 0} items • Created {formatDate(q.created_at)}</p>
                          <p className="text-lg font-bold mt-1">${q.total?.toFixed(2)}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${statusConfig[q.status]?.color}`}>{statusConfig[q.status]?.label}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3 items-center">
                        <Button size="sm" variant="outline" onClick={() => handleViewQuote(q)}><Eye className="h-4 w-4 mr-1" /> View</Button>
                        <Button size="sm" variant="outline" onClick={() => handleEditQuote(q)}><Edit className="h-4 w-4 mr-1" /> Edit</Button>
                        <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(q.id, 'quote')}><Download className="h-4 w-4 mr-1" /> PDF</Button>
                        <Button size="sm" variant="outline" onClick={() => handleDuplicateQuote(q.id)}><FileText className="h-4 w-4 mr-1" /> Duplicate</Button>
                        <div className="flex items-center gap-2 ml-2 px-2 py-1 rounded bg-secondary/40 border border-border">
                          <Label htmlFor={`quote-gst-${q.id}`} className="text-xs cursor-pointer">Apply GST</Label>
                          <Switch
                            id={`quote-gst-${q.id}`}
                            checked={q.charge_gst !== false}
                            onCheckedChange={(v) => handleToggleQuoteGst(q, v)}
                            data-testid={`quote-gst-toggle-${q.id}`}
                          />
                        </div>
                        <Button size="sm" onClick={() => openPODialog(q)}><ShoppingCart className="h-4 w-4 mr-1" /> Generate PO</Button>
                        <Button size="sm" variant="ghost" className="text-destructive ml-auto" onClick={() => handleDeleteQuote(q.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Purchase Orders Tab */}
        <TabsContent value="pos">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Purchase Orders</CardTitle>
              <Button size="sm" onClick={() => setGenPoFromJobOpen(true)} data-testid="generate-po-from-job-btn">
                <ShoppingCart className="h-4 w-4 mr-1" /> Generate PO
              </Button>
            </CardHeader>
            <CardContent>
              {purchase_orders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No purchase orders yet</p>
                  <p className="text-xs mt-2">Click "Generate PO" to allocate items{job?.imported_from_po?.po_number ? ' from the imported client PO' : ' from the latest quote'} to your contractors.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {purchase_orders.map(po => (
                    <div key={po.id} className={`border border-border rounded-lg p-4 ${po.status === 'cancelled' ? 'opacity-60' : 'hover:bg-secondary/20'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{po.po_number}</h4>
                          <p className="text-sm text-muted-foreground">{po.contractor_name || 'Unknown Contractor'}</p>
                          <p className="text-xs text-muted-foreground">{po.items?.length || 0} items</p>
                          <p className="text-lg font-bold mt-1">${po.total?.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded text-xs ${statusConfig[po.status]?.color}`}>{statusConfig[po.status]?.label}</span>
                          {po.cancelled_at && <p className="text-xs text-muted-foreground mt-1">Cancelled {formatDate(po.cancelled_at)}</p>}
                        </div>
                      </div>
                      {po.status !== 'cancelled' && (
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" variant="outline" onClick={() => handleEditPO(po)}><Edit className="h-4 w-4 mr-1" /> Edit</Button>
                          <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(po.id, 'po')}><Download className="h-4 w-4 mr-1" /> PDF</Button>
                          <Button size="sm" onClick={() => handleSendPO(po)}><Send className="h-4 w-4 mr-1" /> Send</Button>
                          <Button size="sm" variant="destructive" onClick={() => { setCancellingPo(po); setCancelPoOpen(true); }}><Ban className="h-4 w-4 mr-1" /> Cancel</Button>
                        </div>
                      )}
                      {po.cancelled_reason && <p className="text-xs text-muted-foreground mt-2 italic">Reason: {po.cancelled_reason}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <InvoicesTab
            job={job}
            quotes={quotes}
            invoices={invoices}
            jobId={jobId}
            statusConfig={statusConfig}
            formatDate={formatDate}
            onDownloadPdf={handleDownloadPdf}
            onChanged={fetchJobData}
          />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-1 overflow-x-auto pb-2">
              {jobDocumentTabs.map(tab => (
                <Button key={tab.id} variant={activeDocTab === tab.id ? 'default' : 'ghost'} size="sm" onClick={() => setActiveDocTab(tab.id)} className="whitespace-nowrap">{tab.label}</Button>
              ))}
            </div>
            <Button onClick={() => setUploadOpen(true)} size="sm"><Upload className="h-4 w-4 mr-2" />Upload</Button>
          </div>
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              {documents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mb-4 opacity-50" /><p>No documents in this category</p>
                  <Button variant="link" onClick={() => setUploadOpen(true)}>Upload a document</Button>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {documents.map(doc => (
                    <div key={doc.id} className="p-4 flex items-center justify-between hover:bg-secondary/20">
                      <div className="flex items-center gap-3">
                        {getFileIcon(doc.mime_type, doc.original_filename)}
                        <div><p className="font-medium">{doc.original_filename}</p><p className="text-xs text-muted-foreground">{formatFileSize(doc.file_size)} • {formatDate(doc.created_at)}</p></div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleDownload(doc)}><Download className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(doc)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-lg">Activity Log</CardTitle></CardHeader>
            <CardContent>
              {activity.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground"><Activity className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No activity recorded yet</p></div>
              ) : (
                <div className="space-y-4">
                  {activity.map(item => (
                    <div key={item.id} className="flex items-start gap-3 pb-4 border-b border-border last:border-0">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0"><Activity className="h-4 w-4 text-primary" /></div>
                      <div className="flex-1">
                        <p className="text-sm"><span className="font-medium">{item.user_name}</span> {item.action} <span className="text-primary">{item.entity_type}</span></p>
                        {item.details && <p className="text-xs text-muted-foreground mt-1">{item.details}</p>}
                        <p className="text-xs text-muted-foreground mt-1">{formatDateTime(item.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Emails Tab */}
        <TabsContent value="emails">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Job Emails</CardTitle>
              <Button size="sm" onClick={() => setEmailOpen(true)}><Send className="h-4 w-4 mr-2" />Send Email</Button>
            </CardHeader>
            <CardContent>
              {emails.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground"><Mail className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No emails sent for this job yet</p></div>
              ) : (
                <div className="divide-y divide-border">
                  {emails.map(email => (
                    <div key={email.id} className="py-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium">{email.subject}</p>
                        <span className={`px-2 py-0.5 rounded text-xs ${email.status === 'sent' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{email.status}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">To: {email.recipient_email}</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatDateTime(email.sent_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle>Upload to {jobDocumentTabs.find(t => t.id === activeDocTab)?.label}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><label className="block text-sm font-medium mb-2">File</label><Input type="file" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="bg-background" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" /></div>
            <div><label className="block text-sm font-medium mb-2">Description</label><Input value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)} placeholder="Brief description..." className="bg-background" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button><Button onClick={handleUpload} disabled={uploading || !uploadFile}>{uploading ? 'Uploading...' : 'Upload'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle>Send Email - {job.title}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><label className="block text-sm font-medium mb-2">Template</label>
              <Select value={emailData.template} onValueChange={handleTemplateChange}>
                <SelectTrigger className="bg-background"><SelectValue placeholder="Select template or custom" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom Email</SelectItem>
                  <SelectItem value="quote_sent">Quote Sent</SelectItem>
                  <SelectItem value="invoice_due">Invoice Due</SelectItem>
                  <SelectItem value="job_update">Job Update</SelectItem>
                  <SelectItem value="po_sent">PO Sent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><label className="block text-sm font-medium mb-2">To</label><Input value={emailData.recipient} onChange={(e) => setEmailData({ ...emailData, recipient: e.target.value })} placeholder="recipient@email.com" className="bg-background" /></div>
            <div><label className="block text-sm font-medium mb-2">Subject</label><Input value={emailData.subject} onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })} className="bg-background" /></div>
            <div><label className="block text-sm font-medium mb-2">Message</label><Textarea value={emailData.body} onChange={(e) => setEmailData({ ...emailData, body: e.target.value })} rows={5} className="bg-background" /></div>
            
            {/* Attachments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4" /> Attachments</label>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={handleOpenAttachments}><FileText className="h-4 w-4 mr-1" /> From Job</Button>
                  <label className="inline-flex items-center px-3 py-1 text-sm border border-input rounded-md cursor-pointer hover:bg-accent">
                    <Upload className="h-4 w-4 mr-1" /> Upload
                    <input type="file" multiple className="hidden" onChange={handleFileUpload} />
                  </label>
                </div>
              </div>
              {(emailAttachments.length > 0 || emailFileUploads.length > 0) ? (
                <div className="border border-border rounded p-2 space-y-1 bg-muted/30">
                  {emailAttachments.map((docId) => {
                    const doc = availableAttachments.find(d => d.id === docId);
                    return (
                      <div key={docId} className="flex items-center justify-between text-sm">
                        <span className="truncate flex items-center gap-2"><FileText className="h-3 w-3" />{doc?.filename || 'Document'}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEmailAttachments(emailAttachments.filter(id => id !== docId))}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                      </div>
                    );
                  })}
                  {emailFileUploads.map((file, index) => (
                    <div key={`upload-${index}`} className="flex items-center justify-between text-sm">
                      <span className="truncate flex items-center gap-2"><Upload className="h-3 w-3 text-primary" />{file.name} <span className="text-xs text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span></span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeUploadedFile(index)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No attachments. Select from job documents or upload from your computer.</p>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button><Button onClick={handleSendEmail} disabled={emailSending}>{emailSending ? 'Sending...' : 'Send Email'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attachment Selection Dialog */}
      <Dialog open={attachmentsDialogOpen} onOpenChange={setAttachmentsDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Select Documents to Attach</DialogTitle></DialogHeader>
          <div className="space-y-2 py-4">
            {availableAttachments.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No documents available for this job</p>
            ) : (
              availableAttachments.map(doc => (
                <div
                  key={doc.id}
                  className={`flex items-center gap-3 p-3 rounded cursor-pointer border ${emailAttachments.includes(doc.id) ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/50'}`}
                  onClick={() => {
                    if (emailAttachments.includes(doc.id)) {
                      setEmailAttachments(emailAttachments.filter(id => id !== doc.id));
                    } else {
                      setEmailAttachments([...emailAttachments, doc.id]);
                    }
                  }}
                >
                  <input type="checkbox" checked={emailAttachments.includes(doc.id)} readOnly className="rounded" />
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">{doc.filename}</p>
                    <p className="text-xs text-muted-foreground">{doc.type}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachmentsDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => setAttachmentsDialogOpen(false)}>Done ({emailAttachments.length} selected)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote Creation Dialog with Sections */}
      <Dialog open={quoteOpen} onOpenChange={(open) => { if (!open) { setQuoteOpen(false); setEditingQuoteId(null); } else { setQuoteOpen(true); } }}>
        <DialogContent className="bg-card border-border max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingQuoteId ? 'Edit Quote' : 'Create Quote'} - {job.title}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">Client: {job.client_name}</p>
            
            {/* Template Selector */}
            <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                Load Quote Template
              </label>
              <div className="flex gap-2">
                <Select 
                  value={selectedTemplate} 
                  onValueChange={(templateId) => {
                    const template = quoteTemplates.find(t => t.id === templateId);
                    if (template && template.sections) {
                      // Load template sections into quote
                      const loadedSections = template.sections.map(section => ({
                        section_type: section.section_type || 'custom',
                        section_name: section.section_name || '',
                        items: section.items.map(item => ({
                          description: item.description || '',
                          quantity: item.quantity || 1,
                          unit_price: item.unit_price || 0,
                          trade_type: item.trade_type || 'General',
                          unit: item.unit || 'Item',
                          buy_rate: item.buy_rate || 0
                        }))
                      }));
                      setQuoteSectionsData(loadedSections);
                      setSelectedTemplate(templateId);
                      toast.success(`Loaded template: ${template.name}`);
                    }
                  }}
                >
                  <SelectTrigger className="flex-1 bg-background">
                    <SelectValue placeholder="Select a template to pre-populate quote..." />
                  </SelectTrigger>
                  <SelectContent>
                    {quoteTemplates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex items-center justify-between gap-4">
                          <span className="font-medium">{template.name}</span>
                          <span className="text-xs text-muted-foreground">{template.category}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setQuoteSectionsData([{ section_type: 'preliminaries', section_name: '', items: [{ description: '', quantity: 1, unit: 'Item', unit_price: 0, trade_type: 'General' }] }]);
                    setSelectedTemplate('');
                    toast.success('Quote cleared');
                  }}
                >
                  Clear
                </Button>
              </div>
              {selectedTemplate && (
                <p className="text-xs text-muted-foreground mt-2">
                  Template loaded - all quantities and prices can be customized for this job
                </p>
              )}
            </div>
            
            {/* Quick Add from Pricing Schedule */}
            <div className="p-3 bg-muted/50 rounded-lg border">
              <label className="block text-sm font-medium mb-2">Quick Add from Schedule of Rates</label>
              <div className="grid grid-cols-4 gap-2">
                <Select value={selectedPricingCategory} onValueChange={setSelectedPricingCategory}>
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="Filter by category..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {[...new Set(pricingItems.map(p => p.category).filter(Boolean))].sort().map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value="" 
                  onValueChange={(itemId) => {
                    const pricingItem = pricingItems.find(p => p.id === itemId);
                    if (pricingItem && quoteSectionsData.length > 0) {
                      // Map pricing category to trade type
                      let tradeType = 'General';
                      if (pricingItem.category?.includes('ELECTRIC')) tradeType = 'Electrician';
                      else if (pricingItem.category?.includes('PLASTER')) tradeType = 'Plasterer';
                      else if (pricingItem.category?.includes('CARPENTER')) tradeType = 'Carpenter';
                      else if (pricingItem.category?.includes('PAINT')) tradeType = 'Painter';
                      else if (pricingItem.category?.includes('PLUMB')) tradeType = 'Plumber';
                      else if (pricingItem.category?.includes('TILER')) tradeType = 'Tiler';
                      else if (pricingItem.category?.includes('DEMOL')) tradeType = 'Labourer';
                      else if (pricingItem.category?.includes('LABOUR')) tradeType = 'Labourer';
                      
                      const newItem = {
                        description: pricingItem.name + (pricingItem.description ? ` - ${pricingItem.description}` : ''),
                        quantity: 1,
                        unit_price: pricingItem.sell_rate || pricingItem.unit_price || 0,
                        trade_type: tradeType,
                        unit: pricingItem.unit || 'Item',
                        buy_rate: pricingItem.buy_rate || 0
                      };
                      // Add to last section
                      const lastSectionIdx = quoteSectionsData.length - 1;
                      const updatedSections = [...quoteSectionsData];
                      updatedSections[lastSectionIdx].items.push(newItem);
                      setQuoteSectionsData(updatedSections);
                      toast.success(`Added: ${pricingItem.name}`);
                    }
                  }}
                >
                  <SelectTrigger className="col-span-3 bg-background">
                    <SelectValue placeholder="Select item from pricing schedule..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {pricingItems
                      .filter(p => selectedPricingCategory === 'all' || p.category === selectedPricingCategory)
                      .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
                      .map(item => (
                        <SelectItem key={item.id} value={item.id}>
                          <div className="flex items-center justify-between w-full gap-4">
                            <span className="truncate">
                              {item.code && <span className="text-muted-foreground mr-2">{item.code}</span>}
                              {item.name}
                            </span>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              ${(item.sell_rate || item.unit_price || 0).toFixed(2)}/{item.unit || 'Item'}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Accordion type="multiple" defaultValue={['section-0']} className="space-y-2" data-testid="quote-sections-accordion">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
                <SortableContext items={sectionsWithDndIds.map((s) => s._dnd_id)} strategy={verticalListSortingStrategy}>
                  {sectionsWithDndIds.map((section, sectionIdx) => (
                    <SortableSectionRow key={section._dnd_id} id={section._dnd_id}>
                      {(dragHandleProps) => (
                <AccordionItem value={`section-${sectionIdx}`} className="border border-border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        {...dragHandleProps}
                        onClick={(e) => e.stopPropagation()}
                        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-1 -ml-1"
                        title="Drag to reorder section"
                        data-testid={`section-drag-${sectionIdx}`}
                        aria-label="Drag to reorder section"
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                      <span className="font-medium">
                        {section.section_type === 'custom' ? section.section_name || 'Custom Section' : sectionLabels[section.section_type]}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ({section.items.length} items - ${section.items.reduce((sum, i) => sum + parseFloat(i.quantity || 0) * parseFloat(i.unit_price || 0), 0).toFixed(2)})
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-2">
                      <div className="flex gap-2 items-center">
                        <Select value={section.section_type} onValueChange={(v) => updateSection(sectionIdx, 'section_type', v)}>
                          <SelectTrigger className="w-40 bg-background"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {quoteSections.map(s => <SelectItem key={s} value={s}>{sectionLabels[s] || s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder={section.section_type === 'custom' ? 'Custom section name' : 'Display name (e.g. Bed 1, Bath 2)'}
                          value={section.section_name}
                          onChange={(e) => updateSection(sectionIdx, 'section_name', e.target.value)}
                          className="bg-background flex-1"
                          data-testid={`section-name-${sectionIdx}`}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => duplicateSection(sectionIdx)}
                          data-testid={`section-duplicate-${sectionIdx}`}
                          title="Duplicate section"
                        >
                          <Copy className="h-4 w-4 mr-1" /> Duplicate
                        </Button>
                        {quoteSectionsData.length > 1 && (
                          <Button variant="ghost" size="icon" onClick={() => removeSection(sectionIdx)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        )}
                      </div>
                      
                      {/* Items in section */}
                      <div className="space-y-2">
                        <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-1">
                          <div className="col-span-1"></div>
                          <div className="col-span-3">Description</div>
                          <div className="col-span-1">Trade</div>
                          <div className="col-span-1">Qty</div>
                          <div className="col-span-1">Unit</div>
                          <div className="col-span-2">Price</div>
                          <div className="col-span-2">Total</div>
                          <div className="col-span-1"></div>
                        </div>
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => {
                            const { active, over } = event;
                            if (active.id !== over?.id && over) {
                              const oldIndex = section.items.findIndex((_, i) => `${sectionIdx}-${i}` === active.id);
                              const newIndex = section.items.findIndex((_, i) => `${sectionIdx}-${i}` === over.id);
                              if (oldIndex !== -1 && newIndex !== -1) {
                                const updated = [...quoteSectionsData];
                                updated[sectionIdx].items = arrayMove(updated[sectionIdx].items, oldIndex, newIndex);
                                setQuoteSectionsData(updated);
                              }
                            }
                          }}
                        >
                          <SortableContext
                            items={section.items.map((_, i) => `${sectionIdx}-${i}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            {section.items.map((item, itemIdx) => (
                              <SortableQuoteItem
                                key={`${sectionIdx}-${itemIdx}`}
                                id={`${sectionIdx}-${itemIdx}`}
                                item={item}
                                itemIdx={itemIdx}
                                sectionIdx={sectionIdx}
                                updateSectionItem={updateSectionItem}
                                removeItemFromSection={removeItemFromSection}
                                canDelete={section.items.length > 1}
                                tradeTypes={tradeTypes}
                                unitTypes={unitTypes}
                                onAddUnit={handleAddUnitType}
                              />
                            ))}
                          </SortableContext>
                        </DndContext>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => addItemToSection(sectionIdx)}><Plus className="h-4 w-4 mr-1" /> Add Blank Item</Button>
                        <Select 
                          value="" 
                          onValueChange={(itemId) => {
                            const pricingItem = pricingItems.find(p => p.id === itemId);
                            if (pricingItem) {
                              let tradeType = 'General';
                              if (pricingItem.category?.includes('ELECTRIC')) tradeType = 'Electrician';
                              else if (pricingItem.category?.includes('PLASTER')) tradeType = 'Plasterer';
                              else if (pricingItem.category?.includes('CARPENTER')) tradeType = 'Carpenter';
                              else if (pricingItem.category?.includes('PAINT')) tradeType = 'Painter';
                              else if (pricingItem.category?.includes('PLUMB')) tradeType = 'Plumber';
                              else if (pricingItem.category?.includes('TILER')) tradeType = 'Tiler';
                              else if (pricingItem.category?.includes('DEMOL')) tradeType = 'Labourer';
                              else if (pricingItem.category?.includes('LABOUR')) tradeType = 'Labourer';
                              
                              const newItem = {
                                description: pricingItem.name + (pricingItem.description ? ` - ${pricingItem.description}` : ''),
                                quantity: 1,
                                unit_price: pricingItem.sell_rate || pricingItem.unit_price || 0,
                                trade_type: tradeType,
                                unit: pricingItem.unit || 'Item',
                                buy_rate: pricingItem.buy_rate || 0
                              };
                              const updatedSections = [...quoteSectionsData];
                              updatedSections[sectionIdx].items.push(newItem);
                              setQuoteSectionsData(updatedSections);
                            }
                          }}
                        >
                          <SelectTrigger className="w-64 bg-background text-sm">
                            <SelectValue placeholder="+ Add from Schedule of Rates" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {pricingItems.sort((a, b) => (a.category || '').localeCompare(b.category || '')).map(item => (
                              <SelectItem key={item.id} value={item.id}>
                                <span className="text-xs text-muted-foreground mr-1">{item.code}</span>
                                {item.name} - ${(item.sell_rate || item.unit_price || 0).toFixed(2)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                      )}
                    </SortableSectionRow>
                  ))}
                </SortableContext>
              </DndContext>
            </Accordion>
            
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={addSection}><Plus className="h-4 w-4 mr-1" /> Add Section</Button>
              <Button variant="outline" onClick={openImportFromQuote} data-testid="import-from-quote-btn"><FileInput className="h-4 w-4 mr-1" /> Import from another quote</Button>
            </div>
            
            {/* Payment Terms and Details */}
            <div className="border-t pt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Payment Terms</label>
                <Input 
                  value={quotePaymentTerms} 
                  onChange={(e) => setQuotePaymentTerms(e.target.value)} 
                  placeholder="e.g., 50% deposit on acceptance, 50% on completion"
                  className="bg-background"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Details (Bank Account)</label>
                <textarea 
                  value={quotePaymentDetails} 
                  onChange={(e) => setQuotePaymentDetails(e.target.value)} 
                  placeholder="Bank: Commonwealth Bank&#10;BSB: 123-456&#10;Account: 12345678&#10;Account Name: Company Name"
                  className="w-full h-24 px-3 py-2 bg-background border border-input rounded-md text-sm resize-none"
                />
              </div>
            </div>
            
            <div className="border-t pt-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Switch
                  id="quote-gst-toggle"
                  checked={quoteChargeGst}
                  onCheckedChange={setQuoteChargeGst}
                  data-testid="quote-gst-toggle"
                />
                <Label htmlFor="quote-gst-toggle" className="cursor-pointer text-sm">
                  Charge GST {quoteChargeGst ? '(10%)' : '— not applicable'}
                </Label>
              </div>
              <div className="text-right">
                <p className="text-sm">Subtotal: ${totals.subtotal.toFixed(2)}</p>
                {quoteChargeGst ? (
                  <p className="text-sm">GST (10%): ${totals.tax.toFixed(2)}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">GST: Not applicable</p>
                )}
                <p className="font-bold text-lg">Total: ${totals.total.toFixed(2)}</p>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setQuoteOpen(false); setEditingQuoteId(null); setQuotePaymentTerms(''); setQuotePaymentDetails(''); setQuoteChargeGst(companyChargesGst); }}>Cancel</Button><Button onClick={handleCreateQuote}>{editingQuoteId ? 'Update Quote' : 'Create Quote'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from Another Quote — picks any existing tenant quote and appends its sections */}
      <Dialog open={importFromQuoteOpen} onOpenChange={setImportFromQuoteOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import sections from another quote</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Search by quote #, client or job…"
              value={importQuoteSearch}
              onChange={(e) => setImportQuoteSearch(e.target.value)}
              className="bg-background"
              data-testid="import-quote-search"
            />
            <p className="text-xs text-muted-foreground">
              Picks all sections from the chosen quote and adds them to this one. Prices are kept — you can edit after import.
            </p>
            <div className="border border-border rounded-md divide-y divide-border max-h-96 overflow-y-auto">
              {allTenantQuotes.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No other quotes available to import from.
                </div>
              ) : (
                allTenantQuotes
                  .filter((q) => {
                    const s = importQuoteSearch.trim().toLowerCase();
                    if (!s) return true;
                    return (
                      (q.quote_number || '').toLowerCase().includes(s)
                      || (q.client_name || '').toLowerCase().includes(s)
                    );
                  })
                  .map((q) => (
                    <button
                      key={q.id}
                      type="button"
                      onClick={() => handleImportFromQuote(q)}
                      className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-secondary/40 transition-colors"
                      data-testid={`import-quote-${q.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-semibold truncate">
                          {q.quote_number} <span className="text-xs text-muted-foreground">· {q.client_name}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {(q.sections?.length || 0)} section{(q.sections?.length || 0) === 1 ? '' : 's'} · {(q.items?.length || 0)} item{(q.items?.length || 0) === 1 ? '' : 's'} · {formatDate(q.created_at)}
                        </div>
                      </div>
                      <div className="font-mono text-sm whitespace-nowrap">${(q.total || 0).toFixed(2)}</div>
                    </button>
                  ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportFromQuoteOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Quote Dialog */}
      <Dialog open={viewQuoteOpen} onOpenChange={setViewQuoteOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selectedQuote?.quote_number}</DialogTitle></DialogHeader>
          {selectedQuote && (
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Status: <span className={`px-2 py-0.5 rounded ${statusConfig[selectedQuote.status]?.color}`}>{statusConfig[selectedQuote.status]?.label}</span></span>
                <span className="text-muted-foreground">Created: {formatDate(selectedQuote.created_at)}</span>
              </div>
              <div className="border rounded-lg p-4 space-y-2">
                {selectedQuote.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.description} {item.section_name && <span className="text-xs text-muted-foreground">({item.section_name})</span>}</span>
                    <span>${(parseFloat(item.quantity || 1) * parseFloat(item.unit_price || 0)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="text-right space-y-1">
                <p className="text-sm">Subtotal: ${selectedQuote.subtotal?.toFixed(2)}</p>
                <p className="text-sm">GST: ${selectedQuote.tax?.toFixed(2)}</p>
                <p className="font-bold">Total: ${selectedQuote.total?.toFixed(2)}</p>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setViewQuoteOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>


      {/* PO from Job (new — bound to job's items) */}
      <GeneratePoFromJobDialog
        open={genPoFromJobOpen}
        onOpenChange={setGenPoFromJobOpen}
        job={job}
        quotes={quotes}
        contractors={contractors}
        tradeTypes={tradeTypes}
        jobId={jobId}
        onSuccess={fetchJobData}
      />

      {/* PO from Quote Dialog - Enhanced with Budget Tracking */}
      <Dialog open={poOpen} onOpenChange={setPoOpen}>
        <DialogContent className="bg-card border-border max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Generate Purchase Orders from Quote {poQuote?.quote_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            
            {/* Budget Summary - Always visible */}
            {(() => {
              const budget = getBudgetSummary();
              return (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-3 bg-muted/50 rounded-lg border">
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Quote Total</div>
                    <div className="text-lg font-bold">${budget.quoteTotal.toFixed(2)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Allocated to POs</div>
                    <div className="text-lg font-bold text-red-500">${budget.allocatedTotal.toFixed(2)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Unallocated</div>
                    <div className={`text-lg font-bold ${budget.unallocatedTotal > 0 ? 'text-yellow-500' : 'text-green-500'}`}>
                      ${budget.unallocatedTotal.toFixed(2)}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Margin</div>
                    <div className={`text-lg font-bold ${parseFloat(budget.margin) > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {budget.margin}%
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Items</div>
                    <div className={`text-lg font-bold ${budget.isFullyAllocated ? 'text-green-500' : 'text-yellow-500'}`}>
                      {budget.allocatedItems}/{budget.totalItems}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Contractor Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Select Contractor to Assign Items</label>
                <Select value={poContractor} onValueChange={setPoContractor}>
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="Choose contractor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contractors.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} - {Array.isArray(c.trade_types) ? c.trade_types.join(', ') : c.trade_type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Filter Items by Trade</label>
                <Select value={poTradeFilter} onValueChange={setPoTradeFilter}>
                  <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Trades</SelectItem>
                    {uniqueTrades.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Quote Items - Select and Assign */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Quote Items 
                <span className="font-normal text-muted-foreground ml-2">(click + to assign remaining qty to selected contractor)</span>
              </label>
              <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left p-2 w-2/5">Description</th>
                      <th className="text-left p-2">Trade</th>
                      <th className="text-center p-2">Remaining</th>
                      <th className="text-right p-2">Price</th>
                      <th className="text-right p-2">Total</th>
                      <th className="text-center p-2 w-16">Add</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(poQuote?.flatItems || poQuote?.items || [])
                      .filter(item => poTradeFilter === 'all' || item.trade_type === poTradeFilter)
                      .map((item, idx) => {
                        const remainingQty = getRemainingQty(item);
                        const isFullyAllocated = remainingQty <= 0;
                        
                        return (
                          <tr key={item.itemKey || idx} className={`border-t ${isFullyAllocated ? 'bg-green-500/10' : ''}`}>
                            <td className="p-2">
                              <div className="font-medium">{item.description}</div>
                              {item.section_name && <div className="text-xs text-muted-foreground">{item.section_name}</div>}
                            </td>
                            <td className="p-2">
                              <span className="px-2 py-0.5 bg-secondary rounded text-xs">{item.trade_type || 'General'}</span>
                            </td>
                            <td className="p-2 text-center">
                              {isFullyAllocated ? (
                                <span className="text-green-500 font-medium">✓ Done</span>
                              ) : (
                                <span className="font-mono">
                                  <span className="text-yellow-500 font-bold">{remainingQty}</span>
                                  <span className="text-muted-foreground"> / {item.quantity || 1}</span>
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-right font-mono">${parseFloat(item.unit_price || 0).toFixed(2)}</td>
                            <td className="p-2 text-right font-mono">${(parseFloat(item.quantity || 1) * parseFloat(item.unit_price || 0)).toFixed(2)}</td>
                            <td className="p-2 text-center">
                              {!isFullyAllocated && (
                                <Button 
                                  size="icon" 
                                  variant="outline" 
                                  className="h-7 w-7 hover:bg-primary hover:text-primary-foreground border-primary/50"
                                  onClick={() => addItemAllocation(item, remainingQty)}
                                  disabled={!poContractor}
                                  title={poContractor ? `Add ${remainingQty} to contractor` : 'Select a contractor first'}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Current Allocations - Grouped by Contractor */}
            {itemAllocations.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  PO Allocations 
                  <span className="font-normal text-muted-foreground ml-2">(each contractor = 1 PO)</span>
                </label>
                
                {/* Group allocations by contractor */}
                {Object.entries(getAllocationTotals()).map(([contractorId, data]) => {
                  const contractorAllocations = itemAllocations.filter(a => a.contractor_id === contractorId);
                  const trades = [...data.trades].join(', ');
                  
                  return (
                    <div key={contractorId} className="mb-3 border rounded-lg overflow-hidden">
                      <div className="bg-primary/10 p-2 flex items-center justify-between">
                        <div>
                          <span className="font-bold">{data.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">({trades})</span>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-primary">${data.total.toFixed(2)}</span>
                          <span className="text-xs text-muted-foreground ml-2">{data.items} item(s)</span>
                        </div>
                      </div>
                      <table className="w-full text-sm">
                        <tbody>
                          {contractorAllocations.map((alloc, idx) => {
                            const globalIdx = itemAllocations.findIndex(a => a === alloc);
                            return (
                              <tr key={idx} className="border-t">
                                <td className="p-2 w-[30%]">
                                  <Input
                                    value={alloc.description || ''}
                                    onChange={(e) => updateAllocation(globalIdx, 'description', e.target.value)}
                                    className="h-7 bg-background"
                                    data-testid={`alloc-desc-${globalIdx}`}
                                  />
                                </td>
                                <td className="p-2 w-20">
                                  <Input 
                                    type="number" 
                                    value={alloc.quantity} 
                                    onChange={(e) => updateAllocation(globalIdx, 'quantity', e.target.value)}
                                    className="h-7 w-16 bg-background text-right"
                                    min="0.01"
                                    step="0.01"
                                  />
                                </td>
                                <td className="p-2 w-20">
                                  <select
                                    value={alloc.unit || 'Item'}
                                    onChange={(e) => updateAllocation(globalIdx, 'unit', e.target.value)}
                                    className="h-7 w-full bg-background border border-border rounded-sm px-1 text-xs"
                                    data-testid={`alloc-unit-${globalIdx}`}
                                  >
                                    {['Item','m2','lm','pc','ps','hour','day','sqm','m','kg','L','set','lot','allow'].map(u => <option key={u} value={u}>{u}</option>)}
                                  </select>
                                </td>
                                <td className="p-2 w-24">
                                  <Input 
                                    type="number" 
                                    value={alloc.unit_price} 
                                    onChange={(e) => updateAllocation(globalIdx, 'unit_price', e.target.value)}
                                    className="h-7 w-20 bg-background text-right"
                                    min="0"
                                    step="0.01"
                                  />
                                </td>
                                <td className="p-2 w-28">
                                  <select
                                    value={alloc.trade_type || 'General'}
                                    onChange={(e) => updateAllocation(globalIdx, 'trade_type', e.target.value)}
                                    className="h-7 w-full bg-background border border-border rounded-sm px-1 text-xs"
                                    data-testid={`alloc-trade-${globalIdx}`}
                                  >
                                    {['General','Plumber','Electrician','Carpenter','Painter','Tiler','Roofer','Plasterer','Bricklayer','Landscaper','HVAC','Glazier','Flooring','Demolition','Concrete'].map(t => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                </td>
                                <td className="p-2 w-24 text-right font-mono font-medium">
                                  ${(alloc.quantity * alloc.unit_price).toFixed(2)}
                                </td>
                                <td className="p-2 w-12 text-center">
                                  <Button 
                                    size="icon" 
                                    variant="ghost" 
                                    className="h-7 w-7 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                                    onClick={() => removeAllocation(globalIdx)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Warning if not fully allocated */}
            {(() => {
              const budget = getBudgetSummary();
              if (itemAllocations.length > 0 && !budget.isFullyAllocated) {
                return (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-500" />
                    <span className="text-sm">
                      <strong>{budget.unallocatedItems} item(s)</strong> not yet allocated (${budget.unallocatedTotal.toFixed(2)} unassigned)
                    </span>
                  </div>
                );
              }
              return null;
            })()}

            {/* Additional Items */}
            <div>
              <label className="block text-sm font-medium mb-2">Add Extra Items (to selected contractor)</label>
              <Select onValueChange={(v) => { const item = commonPOItems.find(i => i.description === v); if (item) addAdditionalItem(item); }}>
                <SelectTrigger className="bg-background"><SelectValue placeholder="Add common items..." /></SelectTrigger>
                <SelectContent>{commonPOItems.map(item => <SelectItem key={item.description} value={item.description}>{item.description}</SelectItem>)}</SelectContent>
              </Select>
              {additionalItems.length > 0 && (
                <div className="mt-2 space-y-1">
                  {additionalItems.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-secondary/30 p-2 rounded">
                      <span>{item.description}</span>
                      <div className="flex items-center gap-2">
                        <Input type="number" value={item.quantity} onChange={(e) => { const updated = [...additionalItems]; updated[idx].quantity = parseFloat(e.target.value) || 1; setAdditionalItems(updated); }} className="w-16 h-7 bg-background" />
                        <Input type="number" value={item.unit_price} onChange={(e) => { const updated = [...additionalItems]; updated[idx].unit_price = parseFloat(e.target.value) || 0; setAdditionalItems(updated); }} className="w-20 h-7 bg-background" />
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setAdditionalItems(additionalItems.filter((_, i) => i !== idx))}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoOpen(false)}>Cancel</Button>
            <Button onClick={handleGeneratePO} disabled={itemAllocations.length === 0}>
              <ShoppingCart className="h-4 w-4 mr-2" />
              Generate {Object.keys(getAllocationTotals()).length} PO(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel PO Dialog */}
      <Dialog open={cancelPoOpen} onOpenChange={setCancelPoOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="h-5 w-5" /> Cancel Purchase Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm">Are you sure you want to cancel <strong>{cancellingPo?.po_number}</strong>?</p>
            <div>
              <label className="block text-sm font-medium mb-2">Reason for Cancellation</label>
              <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Enter reason..." className="bg-background" rows={3} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="sendEmail" checked={sendCancelEmail} onChange={(e) => setSendCancelEmail(e.target.checked)} className="rounded" />
              <label htmlFor="sendEmail" className="text-sm">Send cancellation email to contractor</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelPoOpen(false)}>Keep PO</Button>
            <Button variant="destructive" onClick={handleCancelPO}>Cancel PO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit PO Dialog */}
      <Dialog open={editPoOpen} onOpenChange={setEditPoOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-primary" />
              Edit Purchase Order - {editingPo?.po_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">Contractor: {editingPo?.contractor_name}</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Line Items</label>
                <Button size="sm" variant="outline" onClick={() => setEditPoItems([...editPoItems, { description: '', quantity: 1, unit_price: 0 }])}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2 border border-border rounded p-3">
                {editPoItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => {
                        const updated = [...editPoItems];
                        updated[index].description = e.target.value;
                        setEditPoItems(updated);
                      }}
                      className="col-span-6 bg-input border-border text-sm"
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => {
                        const updated = [...editPoItems];
                        updated[index].quantity = parseFloat(e.target.value) || 0;
                        setEditPoItems(updated);
                      }}
                      className="col-span-2 bg-input border-border text-sm"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={(e) => {
                        const updated = [...editPoItems];
                        updated[index].unit_price = parseFloat(e.target.value) || 0;
                        setEditPoItems(updated);
                      }}
                      className="col-span-3 bg-input border-border text-sm"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditPoItems(editPoItems.filter((_, i) => i !== index))}
                      className="col-span-1"
                      disabled={editPoItems.length === 1}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                <div className="flex justify-end pt-2 border-t border-border">
                  <p className="font-mono font-bold">
                    Total: ${(editPoItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0) * 1.1).toFixed(2)} (inc GST)
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPoOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdatePO}>Update PO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send PO Dialog */}
      <Dialog open={sendPoOpen} onOpenChange={setSendPoOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" />
              Send Purchase Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm font-medium">PO Number:</p>
              <p className="text-lg font-bold">{sendingPo?.po_number}</p>
            </div>
            <div>
              <p className="text-sm font-medium">Contractor:</p>
              <p>{sendingPo?.contractor_name}</p>
              <p className="text-sm text-muted-foreground">{sendingPo?.contractor_email || 'No email on file'}</p>
            </div>
            <div>
              <p className="text-sm font-medium">Total:</p>
              <p className="text-lg font-bold">${sendingPo?.total?.toFixed(2)}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Message to Contractor</label>
              <Textarea
                value={poEmailMessage}
                onChange={(e) => setPoEmailMessage(e.target.value)}
                rows={4}
                className="bg-input border-border"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendPoOpen(false)}>Cancel</Button>
            <Button onClick={handleSendPOEmail} disabled={poSending || !sendingPo?.contractor_email}>
              {poSending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : <><Send className="h-4 w-4 mr-2" /> Send PO</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import PDF Dialog */}
      <Dialog open={importPdfOpen} onOpenChange={setImportPdfOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              Import Quote from PDF
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Upload a PDF quote and AI will automatically extract line items and pricing for this job.
            </p>
            <div>
              <label className="block text-sm font-medium mb-2">Select PDF File</label>
              <Input
                type="file"
                accept=".pdf"
                onChange={(e) => setImportPdfFile(e.target.files?.[0] || null)}
                className="bg-input border-border"
              />
              {importPdfFile && (
                <p className="text-xs text-muted-foreground mt-1">Selected: {importPdfFile.name}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportPdfOpen(false); setImportPdfFile(null); }}>Cancel</Button>
            <Button onClick={handleImportPdf} disabled={importPdfLoading || !importPdfFile}>
              {importPdfLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Extracting...</> : <><Sparkles className="h-4 w-4 mr-2" /> Extract Quote</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Trade Suggestions Dialog */}
      <Dialog open={aiSuggestOpen} onOpenChange={setAiSuggestOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Trade Recommendations
            </DialogTitle>
          </DialogHeader>
          {aiTradeSuggestions && (
            <div className="space-y-4 py-4">
              {aiTradeSuggestions.required_trades?.length > 0 && (
                <div className="p-3 bg-primary/10 rounded-lg">
                  <p className="text-sm font-medium mb-1">Trades Needed for this Job:</p>
                  <div className="flex flex-wrap gap-2">
                    {aiTradeSuggestions.required_trades.map((trade, idx) => (
                      <span key={idx} className="px-2 py-1 bg-primary/20 text-primary rounded text-sm">{trade}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-3">
                <p className="text-sm font-medium">Recommended Contractors:</p>
                {aiTradeSuggestions.suggestions?.map((suggestion, idx) => (
                  <Card key={idx} className="bg-secondary/30 border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium">{suggestion.contractor_name}</h4>
                          <p className="text-sm text-muted-foreground">{suggestion.trade_types?.join(', ')}</p>
                        </div>
                        <div className="text-right">
                          <div className={`px-2 py-1 rounded text-sm ${
                            suggestion.match_score >= 80 ? 'bg-green-500/20 text-green-400' :
                            suggestion.match_score >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {suggestion.match_score}% Match
                          </div>
                          <span className={`text-xs ${
                            suggestion.availability === 'high' ? 'text-green-400' :
                            suggestion.availability === 'medium' ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {suggestion.availability} availability
                          </span>
                        </div>
                      </div>
                      <p className="text-sm mt-2 text-muted-foreground">{suggestion.reason}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {aiTradeSuggestions.notes && (
                <p className="text-sm text-muted-foreground italic">{aiTradeSuggestions.notes}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiSuggestOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Cost Estimate Dialog */}
      <Dialog open={aiCostOpen} onOpenChange={setAiCostOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              AI Cost Estimate
            </DialogTitle>
          </DialogHeader>
          {aiCostEstimate && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg">
                <span className="text-lg font-medium">Estimated Total</span>
                <span className="text-2xl font-bold text-primary">${aiCostEstimate.estimated_total?.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Confidence:</span>
                <span className={`px-2 py-1 rounded text-sm ${
                  aiCostEstimate.confidence === 'high' ? 'bg-green-500/20 text-green-400' :
                  aiCostEstimate.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {aiCostEstimate.confidence}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Cost Breakdown:</p>
                <div className="space-y-2">
                  {aiCostEstimate.breakdown?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-secondary/30 rounded text-sm">
                      <div>
                        <span className="font-medium">{item.description}</span>
                        <span className="text-muted-foreground ml-2">({item.category})</span>
                      </div>
                      <span className="font-mono">${item.estimated_cost?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
              {aiCostEstimate.risk_factors?.length > 0 && (
                <div className="p-3 bg-yellow-500/10 rounded-lg">
                  <p className="text-sm font-medium text-yellow-400 mb-1">Risk Factors:</p>
                  <ul className="text-sm list-disc list-inside text-muted-foreground">
                    {aiCostEstimate.risk_factors.map((risk, idx) => <li key={idx}>{risk}</li>)}
                  </ul>
                </div>
              )}
              {aiCostEstimate.recommendations?.length > 0 && (
                <div className="p-3 bg-blue-500/10 rounded-lg">
                  <p className="text-sm font-medium text-blue-400 mb-1">Recommendations:</p>
                  <ul className="text-sm list-disc list-inside text-muted-foreground">
                    {aiCostEstimate.recommendations.map((rec, idx) => <li key={idx}>{rec}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiCostOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Email Draft Dialog */}
      <Dialog open={aiEmailOpen} onOpenChange={(open) => { setAiEmailOpen(open); if (!open) { setAiEmailDraft(null); setAiEmailRecipient(''); } }}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-primary" />
              AI Email Assistant
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email Type</label>
              <Select value={aiEmailType} onValueChange={setAiEmailType}>
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quote_followup">Quote Follow-up</SelectItem>
                  <SelectItem value="payment_reminder">Payment Reminder</SelectItem>
                  <SelectItem value="job_update">Job Update</SelectItem>
                  <SelectItem value="thank_you">Thank You</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Recipient Name</label>
              <Input 
                value={aiEmailRecipient} 
                onChange={(e) => setAiEmailRecipient(e.target.value)}
                placeholder="e.g., Mr. Smith"
                className="bg-background"
              />
            </div>
            {aiEmailDraft && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4 space-y-3">
                  <div>
                    <span className="text-sm text-muted-foreground">Subject:</span>
                    <p className="font-medium">{aiEmailDraft.subject}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Body:</span>
                    <p className="text-sm whitespace-pre-wrap mt-1">{aiEmailDraft.body}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>Tone: {aiEmailDraft.tone}</span>
                    <span>Follow-up in: {aiEmailDraft.suggested_followup_days} days</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiEmailOpen(false)}>Cancel</Button>
            {!aiEmailDraft ? (
              <Button onClick={handleAIDraftEmail} disabled={aiEmailLoading || !aiEmailRecipient}>
                {aiEmailLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><Wand2 className="h-4 w-4 mr-2" />Generate Draft</>}
              </Button>
            ) : (
              <Button onClick={applyAIEmailDraft} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />Use This Draft
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Job Dialog */}
      <Dialog open={editJobOpen} onOpenChange={setEditJobOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Job Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Job Title</label>
                <Input
                  value={editJobData.title}
                  onChange={(e) => setEditJobData({...editJobData, title: e.target.value})}
                  placeholder="Job title"
                  data-testid="edit-job-title"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={editJobData.description}
                  onChange={(e) => setEditJobData({...editJobData, description: e.target.value})}
                  placeholder="Job description"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Client Name</label>
                <Input
                  value={editJobData.client_name}
                  onChange={(e) => setEditJobData({...editJobData, client_name: e.target.value})}
                  placeholder="Client name"
                  data-testid="edit-job-client-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Client Email</label>
                <Input
                  type="email"
                  value={editJobData.client_email}
                  onChange={(e) => setEditJobData({...editJobData, client_email: e.target.value})}
                  placeholder="client@email.com"
                  data-testid="edit-job-client-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Client Phone</label>
                <Input
                  value={editJobData.client_phone}
                  onChange={(e) => setEditJobData({...editJobData, client_phone: e.target.value})}
                  placeholder="Phone number"
                  data-testid="edit-job-client-phone"
                />
              </div>
              <div className="col-span-2 pt-2 border-t border-border/60">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                  Customer <span className="font-normal normal-case">(site contact — not billed)</span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Customer Name</label>
                <Input
                  value={editJobData.customer_name || ''}
                  onChange={(e) => setEditJobData({...editJobData, customer_name: e.target.value})}
                  placeholder="e.g. homeowner at the site"
                  data-testid="edit-job-customer-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Customer Email</label>
                <Input
                  type="email"
                  value={editJobData.customer_email || ''}
                  onChange={(e) => setEditJobData({...editJobData, customer_email: e.target.value})}
                  placeholder="customer@email.com"
                  data-testid="edit-job-customer-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Customer Phone</label>
                <Input
                  value={editJobData.customer_phone || ''}
                  onChange={(e) => setEditJobData({...editJobData, customer_phone: e.target.value})}
                  placeholder="Phone number"
                  data-testid="edit-job-customer-phone"
                />
              </div>
              <div className="col-span-2 pt-2 border-t border-border/60" />
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <Input
                  value={editJobData.address}
                  onChange={(e) => setEditJobData({...editJobData, address: e.target.value})}
                  placeholder="Job address"
                  data-testid="edit-job-address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Start Date</label>
                <DatePicker
                  value={editJobData.start_date}
                  onChange={(d) => setEditJobData({...editJobData, start_date: d})}
                  placeholder="Start date"
                  data-testid="edit-job-start-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End Date</label>
                <DatePicker
                  value={editJobData.end_date}
                  onChange={(d) => setEditJobData({...editJobData, end_date: d})}
                  placeholder="End date"
                  data-testid="edit-job-end-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Budget ($)</label>
                <Input
                  type="number"
                  value={editJobData.budget}
                  onChange={(e) => setEditJobData({...editJobData, budget: parseFloat(e.target.value) || ''})}
                  placeholder="0.00"
                />
              </div>
              <div className="col-span-2 pt-2 border-t border-border/60">
                <label className="block text-sm font-medium mb-1">Client prefix (for document numbering)</label>
                <Input
                  value={editJobData.client_prefix}
                  onChange={(e) => setEditJobData({...editJobData, client_prefix: e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6)})}
                  placeholder="e.g. ARC"
                  maxLength={6}
                  data-testid="edit-job-client-prefix"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  When set, quotes/invoices/POs on this job get numbers like
                  <code className="mx-1 text-foreground">Q{editJobData.client_prefix || 'ARC'}-1001</code>
                  <code className="mx-1 text-foreground">INV{editJobData.client_prefix || 'ARC'}-1001</code>
                  <code className="mx-1 text-foreground">PO{editJobData.client_prefix || 'ARC'}-1001</code>.
                  Leave blank for the default dated format.
                </p>
              </div>
              <div className="col-span-2 pt-2 border-t border-border/60">
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="edit-job-is-roof-toggle">
                  <input
                    type="checkbox"
                    checked={!!editJobData.is_roof}
                    onChange={(e) => setEditJobData({...editJobData, is_roof: e.target.checked})}
                    className="h-4 w-4 accent-orange-500"
                  />
                  <span className="text-sm">This is a roof job (shows a Roof badge in the job list)</span>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditJobOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateJob} disabled={editJobLoading} data-testid="save-job-btn">
              {editJobLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Client Portal Link Dialog */}
      <Dialog open={sendLinkOpen} onOpenChange={setSendLinkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Client Portal Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Share the client portal link with your client so they can view their quotes and project details.
            </p>
            {getClientPortalLink() ? (
              <div className="space-y-4">
                <div className="p-3 bg-secondary rounded-md">
                  <p className="text-xs text-muted-foreground mb-1">Portal Link:</p>
                  <p className="text-sm break-all">{getClientPortalLink()}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={handleCopyLink} data-testid="copy-link-btn">
                    <Copy className="h-4 w-4 mr-2" /> Copy Link
                  </Button>
                  <Button 
                    className="flex-1" 
                    onClick={handleSendPortalLink} 
                    disabled={sendLinkLoading || !job?.client_email}
                    data-testid="email-link-btn"
                  >
                    {sendLinkLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                    ) : (
                      <><Mail className="h-4 w-4 mr-2" /> Email to Client</>
                    )}
                  </Button>
                </div>
                {!job?.client_email && (
                  <p className="text-xs text-yellow-500">No client email address. Add one to send via email.</p>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted-foreground">No client portal link available yet.</p>
                <p className="text-sm text-muted-foreground mt-1">Create a quote first to generate a portal link.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendLinkOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetailPage;
